import React, { useState, useEffect, useMemo } from 'react';
import subUserService from '../../services/SubUserService';
import studentService from '../../services/StudentService';
import brandingService from '../../services/BrandingService';
import identifierService from '../../services/IdentifierService';
import authService from '../../services/AuthService';
import { API_BASE_URL } from '../../config.js';
import { 
  Building, Image, Hash, BookOpen, Users, Shield, CreditCard, Lock, Save, RefreshCw, Upload, Trash2, CheckCircle2, AlertCircle, Eye, FileText, HardDrive, MessageSquare, Smartphone
} from 'lucide-react';
import ReportCardTemplateDesigner from './ReportCardTemplateDesigner.jsx';
import StaffUserManagement from './StaffUserManagement.js';
import PermissionsMatrix from './PermissionsMatrix.js';
import PaymentBankSettings from './PaymentBankSettings.js';
import StorageManagement from './StorageManagement.jsx';
import SmartAttendanceService from '../../services/SmartAttendanceService';

import { getNormalizedInstitutionType } from '../../config/institutionConfig';

const SettingsManagement = ({ orgData, showNotification, onLogout }) => {
  const [activeSubTab, setActiveSubTab] = useState('profile'); // 'profile', 'branding', 'student_id', 'roll_no', 'academic', 'users', 'permissions', 'payment', 'security', 'whatsapp'

  // WhatsApp & SMS Gateway State
  const [whatsappConfig, setWhatsappConfig] = useState({
    enableWhatsapp: true,
    enableSms: false,
    whatsappProvider: 'CUSTOM_GATEWAY',
    whatsappApiUrl: '',
    whatsappApiKey: '',
    whatsappSubUser: '',
    whatsappSenderNumber: '',
    whatsappAbsentTemplate: 'Dear Parent, your ward {{studentName}} (Class {{className}}-{{section}}, Roll No: {{rollNo}}) is marked ABSENT today ({{date}}). If this is an error, please contact the school office.'
  });
  const [whatsappSaving, setWhatsappSaving] = useState(false);
  const [waTestPhone, setWaTestPhone] = useState('');
  const [waTestLoading, setWaTestLoading] = useState(false);
  const [waTestResult, setWaTestResult] = useState(null);

  useEffect(() => {
    const loadWhatsAppSettings = async () => {
      try {
        const data = await SmartAttendanceService.getSettings();
        if (data && data.id) {
          setWhatsappConfig(prev => ({
            ...prev,
            enableWhatsapp: data.enableWhatsapp ?? true,
            enableSms: data.enableSms ?? false,
            whatsappProvider: data.whatsappProvider || 'CUSTOM_GATEWAY',
            whatsappApiUrl: data.whatsappApiUrl || '',
            whatsappApiKey: data.whatsappApiKey || '',
            whatsappSubUser: data.whatsappSubUser || '',
            whatsappSenderNumber: data.whatsappSenderNumber || '',
            whatsappAbsentTemplate: data.whatsappAbsentTemplate || prev.whatsappAbsentTemplate
          }));
        }
      } catch (err) {
        console.error('Error loading WhatsApp settings:', err);
      }
    };
    loadWhatsAppSettings();
  }, []);

  const handleSaveWhatsApp = async () => {
    setWhatsappSaving(true);
    try {
      const res = await SmartAttendanceService.saveSettings(whatsappConfig);
      if (res && res.id) {
        if (showNotification) showNotification('success', 'Saved', 'WhatsApp API Gateway & Sub-User settings saved successfully');
      }
    } catch (err) {
      if (showNotification) showNotification('error', 'Save Failed', err.message);
    } finally {
      setWhatsappSaving(false);
    }
  };

  const handleTestWhatsAppConnection = async () => {
    if (!waTestPhone.trim()) {
      if (showNotification) showNotification('error', 'Phone Required', 'Please enter a test mobile number');
      return;
    }
    setWaTestLoading(true);
    setWaTestResult(null);
    try {
      const res = await SmartAttendanceService.testWhatsAppConnection(
        waTestPhone.trim(),
        `🔔 [School ERP Test Alert] Connection successful! Your WhatsApp API Gateway and Sub-User (${whatsappConfig.whatsappSubUser || 'Default'}) are properly connected.`
      );
      setWaTestResult(res);
      if (res && res.success) {
        if (showNotification) showNotification('success', 'Test Alert Sent', 'Test WhatsApp message sent successfully!');
      }
    } catch (err) {
      setWaTestResult({ success: false, error: err.message });
      if (showNotification) showNotification('error', 'Test Failed', err.message);
    } finally {
      setWaTestLoading(false);
    }
  };

  // 1. Institution Profile State
  const [profileForm, setProfileForm] = useState({
    trustName: orgData?.trustName || localStorage.getItem('trustName') || '',
    institutionName: orgData?.orgName || orgData?.institutionName || orgData?.name || 'Institution Name',
    institutionType: getNormalizedInstitutionType(orgData),
    orgId: orgData?.orgId || 'SCH-02',
    address: orgData?.address || 'Near Bus Stand, Central Area',
    phone: orgData?.phoneNumber || '',
    email: orgData?.email || '',
    website: orgData?.website || '',
    affiliation: orgData?.affiliation || 'State Board',
    principalName: orgData?.principalName || '',
    allowParentPhotoUpload: orgData?.allowParentPhotoUpload !== undefined ? orgData.allowParentPhotoUpload : true,
    allowTeacherPhotoUpload: orgData?.allowTeacherPhotoUpload !== undefined ? orgData.allowTeacherPhotoUpload : true
  });
  const [profileSaving, setProfileSaving] = useState(false);

  // Sync profile form state when orgData changes
  useEffect(() => {
    if (orgData) {
      setProfileForm(prev => ({
        ...prev,
        trustName: orgData.trustName !== undefined ? orgData.trustName : (localStorage.getItem('trustName') || prev.trustName),
        institutionName: orgData.orgName || orgData.name || prev.institutionName,
        institutionType: getNormalizedInstitutionType(orgData),
        orgId: orgData.orgId || prev.orgId,
        address: orgData.address || prev.address,
        phone: orgData.phoneNumber || prev.phone,
        email: orgData.email || prev.email,
        affiliation: orgData.affiliation || prev.affiliation,
        principalName: orgData.principalName || prev.principalName,
        allowParentPhotoUpload: orgData.allowParentPhotoUpload !== undefined ? orgData.allowParentPhotoUpload : prev.allowParentPhotoUpload,
        allowTeacherPhotoUpload: orgData.allowTeacherPhotoUpload !== undefined ? orgData.allowTeacherPhotoUpload : prev.allowTeacherPhotoUpload
      }));
    }
  }, [orgData]);

  // Student & Parent App Password Management State
  const getActiveInitialDefaultPwd = () => {
    return orgData?.defaultStudentPassword || 
           localStorage.getItem(`erp_default_pwd_${orgData?.orgId || 'default'}`) || 
           localStorage.getItem('erp_default_pwd_default') || 
           'Student@123';
  };

  const [defaultStudentPassword, setDefaultStudentPassword] = useState(getActiveInitialDefaultPwd);
  const [bulkResetPassword, setBulkResetPassword] = useState(getActiveInitialDefaultPwd);
  const [alsoApplyToExisting, setAlsoApplyToExisting] = useState(true);
  const [savingDefaultPassword, setSavingDefaultPassword] = useState(false);
  const [bulkResetting, setBulkResetting] = useState(false);

  // Keep passwords in sync when orgData loads or changes
  useEffect(() => {
    if (orgData) {
      const activeOrgId = orgData.orgId || orgData.id || 'default';
      const activePwd = orgData.defaultStudentPassword || 
                        localStorage.getItem(`erp_default_pwd_${activeOrgId}`) || 
                        localStorage.getItem('erp_default_pwd_default') || 
                        'Student@123';
      setDefaultStudentPassword(activePwd);
      setBulkResetPassword(activePwd);
    }
  }, [orgData]);

  // Save standard default password policy
  const handleSaveDefaultPassword = async () => {
    if (!defaultStudentPassword || !defaultStudentPassword.trim()) {
      if (showNotification) showNotification('error', 'Error', 'Please enter a valid default password');
      return;
    }
    const cleanPwd = defaultStudentPassword.trim();
    setSavingDefaultPassword(true);
    try {
      const activeOrgId = orgData?.orgId || orgData?.id || localStorage.getItem('orgId') || 'default';
      localStorage.setItem(`erp_default_pwd_${activeOrgId}`, cleanPwd);
      localStorage.setItem('erp_default_pwd_default', cleanPwd);

      await authService.updateAdminProfile({ defaultStudentPassword: cleanPwd });
      
      if (orgData) {
        const updatedOrg = { ...orgData, defaultStudentPassword: cleanPwd };
        localStorage.setItem('orgData', JSON.stringify(updatedOrg));
        window.dispatchEvent(new Event('orgDataChanged'));
      }
      setBulkResetPassword(cleanPwd);

      // If user also wants to apply to all existing students and parents now
      if (alsoApplyToExisting) {
        try {
          const resetData = await studentService.bulkResetPasswords(activeOrgId, cleanPwd);
          if (resetData && resetData.success !== false) {
            if (showNotification) {
              showNotification('success', 'Policy & Accounts Updated', resetData.message || `Default password set to "${cleanPwd}" and all existing accounts updated!`);
            }
            return;
          }
        } catch (resetErr) {
          console.error('Failed to reset existing students during policy save:', resetErr);
        }
      }

      if (showNotification) {
        showNotification('success', 'Policy Saved', `Standard default password set to "${cleanPwd}" for new admissions`);
      }
    } catch (err) {
      console.error('Error saving default password policy:', err);
      const activeOrgId = orgData?.orgId || orgData?.id || localStorage.getItem('orgId') || 'default';
      localStorage.setItem(`erp_default_pwd_${activeOrgId}`, cleanPwd);
      if (showNotification) {
        showNotification('success', 'Policy Saved Locally', `Default password set to "${cleanPwd}"`);
      }
    } finally {
      setSavingDefaultPassword(false);
    }
  };

  const handleBulkResetStudentPasswords = async () => {
    if (!bulkResetPassword || !bulkResetPassword.trim()) {
      if (showNotification) showNotification('error', 'Error', 'Please enter a valid password');
      return;
    }

    const cleanPwd = bulkResetPassword.trim();
    const activeOrgId = orgData?.orgId || orgData?.id || localStorage.getItem('orgId') || 'default';

    if (!window.confirm(`Are you sure you want to reset ALL student and parent login passwords to "${cleanPwd}"?\n\nThis will update every student and parent account in your institution (even if parents previously changed their password) so everyone can log in with "${cleanPwd}".`)) {
      return;
    }

    setBulkResetting(true);
    try {
      const data = await studentService.bulkResetPasswords(activeOrgId, cleanPwd);
      if (data && data.success !== false) {
        // Sync both cards, localStorage and orgData state
        setDefaultStudentPassword(cleanPwd);
        setBulkResetPassword(cleanPwd);
        localStorage.setItem(`erp_default_pwd_${activeOrgId}`, cleanPwd);
        localStorage.setItem('erp_default_pwd_default', cleanPwd);

        if (orgData) {
          const updatedOrg = { ...orgData, defaultStudentPassword: cleanPwd };
          localStorage.setItem('orgData', JSON.stringify(updatedOrg));
          window.dispatchEvent(new Event('orgDataChanged'));
        }

        if (showNotification) {
          showNotification('success', 'Reset Completed', data.message || `All student accounts and admission policy updated with password: ${cleanPwd}`);
        }
      } else {
        if (showNotification) {
          showNotification('error', 'Failed', data?.error || data?.message || 'Failed to bulk reset passwords');
        }
      }
    } catch (err) {
      console.error('Error resetting passwords:', err);
      if (showNotification) showNotification('error', 'Error', err.message || 'Could not reset passwords. Please check permissions.');
    } finally {
      setBulkResetting(false);
    }
  };

  // 2. Branding Assets State
  const [branding, setBranding] = useState({
    trustName: orgData?.trustName || localStorage.getItem('trustName') || '',
    logoUrl: '',
    signatureUrl: '',
    sealUrl: '',
    primaryColor: '#1e3a8a',
    secondaryColor: '#f59e0b',
    academicYear: '2026-27'
  });
  const [brandingLoading, setBrandingLoading] = useState(false);
  const [uploadingAsset, setUploadingAsset] = useState('');

  // 3. Student ID Numbering Generator Config State
  const [studentIdConfig, setStudentIdConfig] = useState({
    mode: 'AUTOMATIC', // AUTOMATIC | MANUAL
    prefix: 'SCH',
    includeYear: true,
    yearFormat: 'YYYY', // YYYY | YY
    separator: '-',
    padding: 4, // 3 to 8 digits
    startSeq: 1,
    resetPolicy: 'ACADEMIC_YEAR', // ACADEMIC_YEAR | NEVER
    nextSeq: 1
  });
  const [studentIdSaving, setStudentIdSaving] = useState(false);

  // 3b. Teacher / Faculty ID Generator Config State
  const [teacherIdConfig, setTeacherIdConfig] = useState({
    mode: 'AUTOMATIC',
    prefix: 'TCH',
    includeYear: false,
    separator: '-',
    padding: 3, // e.g. TCH-101
    startSeq: 101,
    resetPolicy: 'NEVER',
    nextSeq: 1
  });

  // 3c. Library Member ID Config State
  const [libraryIdConfig, setLibraryIdConfig] = useState({
    mode: 'AUTOMATIC',
    prefix: 'LIB',
    includeYear: true,
    separator: '-',
    padding: 4,
    startSeq: 1,
    resetPolicy: 'NEVER',
    nextSeq: 1
  });

  // 3d. Fee Receipt ID Config State
  const [receiptIdConfig, setReceiptIdConfig] = useState({
    mode: 'AUTOMATIC',
    prefix: 'RCPT',
    includeYear: true,
    separator: '-',
    padding: 4,
    startSeq: 1,
    resetPolicy: 'ACADEMIC_YEAR',
    nextSeq: 1
  });

  // Reset Sequence Modal States
  const [resetModalOpen, setResetModalOpen] = useState(false);
  const [resetType, setResetType] = useState('STUDENT');
  const [resetReason, setResetReason] = useState('');
  const [resetNextVal, setResetNextVal] = useState(1);
  const [resetForce, setResetForce] = useState(false);
  const [resetWarning, setResetWarning] = useState('');
  const [canForce, setCanForce] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);

  const [dbConfigs, setDbConfigs] = useState([]);

  // 4. Roll Number Settings State
  const [rollNoConfig, setRollNoConfig] = useState({
    rollNumberMode: 'SEQUENTIAL_PER_CLASS',
    assignmentOrdering: 'ALPHABETICAL',
    startingRollNumber: 1,
    padding: 3,
    prefix: '',
    suffix: '',
    separator: '',
    resetPolicy: 'ACADEMIC_YEAR',
    regenerateOnPromotion: true,
    allowManualOverride: true,
    preview: ''
  });

  // Configurable Class Progression Mapping State
  const [classProgressionRules, setClassProgressionRules] = useState([
    { from: 'Nursery', to: 'LKG' },
    { from: 'LKG', to: 'UKG' },
    { from: 'UKG', to: '1st Standard' },
    { from: '1st Standard', to: '2nd Standard' },
    { from: '2nd Standard', to: '3rd Standard' },
    { from: '3rd Standard', to: '4th Standard' },
    { from: '4th Standard', to: '5th Standard' },
    { from: '5th Standard', to: '6th Standard' },
    { from: '6th Standard', to: '7th Standard' },
    { from: '7th Standard', to: '8th Standard' },
    { from: '8th Standard', to: '9th Standard' },
    { from: '9th Standard', to: '10th Standard' },
    { from: '10th Standard', to: 'GRADUATED' }
  ]);
  const [academicYearsList, setAcademicYearsList] = useState(['2026-27', '2025-26', '2024-25', '2027-28']);
  const [newAcademicYearInput, setNewAcademicYearInput] = useState('');

  const handleDeclareAcademicYear = () => {
    if (!newAcademicYearInput || !newAcademicYearInput.trim()) return;
    const yearStr = newAcademicYearInput.trim();
    if (!academicYearsList.includes(yearStr)) {
      setAcademicYearsList(prev => [yearStr, ...prev]);
    }
    setAcademicConfig(prev => ({ ...prev, currentAcademicYear: yearStr }));
    setNewAcademicYearInput('');
    if (showNotification) showNotification('success', 'Academic Year Declared', `Academic Year ${yearStr} set as active session`);
  };

  const [academicConfig, setAcademicConfig] = useState({
    currentAcademicYear: '2026-27',
    institutionType: 'SCHOOL',
    workingDays: 'Mon, Tue, Wed, Thu, Fri, Sat',
    weekStart: 'Monday',
    gradingScheme: 'PERCENTAGE',
    minimumAttendance: 75,
    allowFeeDue: false,
    allowFailedSubjects: false
  });
  const [academicSaving, setAcademicSaving] = useState(false);

  const handleSaveAcademicSettings = async () => {
    try {
      setAcademicSaving(true);
      const orgId = orgData?.orgId || 'SCH-02';
      const payload = {
        academicConfig,
        academicYearsList,
        classProgressionRules
      };
      localStorage.setItem(`academic_settings_${orgId}`, JSON.stringify(payload));

      try {
        await studentService.updatePromotionSettings({
          minimumAttendance: academicConfig.minimumAttendance,
          allowFeeDue: academicConfig.allowFeeDue,
          allowFailedSubjects: academicConfig.allowFailedSubjects
        });
      } catch (err) {
        console.warn('Backend promotion sync warning:', err);
      }

      if (showNotification) {
        showNotification('success', 'Saved Successfully', 'Academic settings, declared years, and class progression rules updated');
      }
    } catch (err) {
      console.error('Save academic settings error:', err);
      if (showNotification) {
        showNotification('error', 'Error', 'Failed to save academic settings');
      }
    } finally {
      setAcademicSaving(false);
    }
  };

  // Save Roll Number Rules
  const handleSaveRollNoRules = async () => {
    try {
      const payload = {
        academicYear: '2026-27',
        ...rollNoConfig
      };
      const response = await identifierService.saveRollNumberSettings(payload);
      
      if (response && response.success) {
        if (response.preview) {
          setRollNoConfig(prev => ({ ...prev, preview: response.preview }));
        }
        if (showNotification) {
          showNotification('success', 'Saved', 'Roll number rules updated successfully');
        }
      } else {
        throw new Error(response.error || 'Failed to save rules');
      }
    } catch (err) {
      console.error('Error saving roll number settings:', err);
      if (showNotification) {
        showNotification('error', 'Error', err.message || 'Failed to save roll number rules');
      }
    }
  };

  const handleRegenerateRollNumbers = async () => {
    try {
      const response = await identifierService.regenerateRollNumbers({ academicYear: '2026-27' });
      if (response && response.success) {
        if (showNotification) {
          showNotification('success', 'Regeneration Initiated', response.message || 'Roll numbers regeneration initiated');
        }
      } else {
        throw new Error(response.error || 'Failed to initiate regeneration');
      }
    } catch (err) {
      console.error('Error regenerating roll numbers:', err);
      if (showNotification) {
        showNotification('error', 'Error', err.message || 'Failed to regenerate roll numbers');
      }
    }
  };

  // 6. Sub-users / Staff State
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [showAddStaffModal, setShowAddStaffModal] = useState(false);
  const [newStaffData, setNewStaffData] = useState({ username: '', email: '', password: '', role: 'TEACHER' });
  const [isCreatingStaff, setIsCreatingStaff] = useState(false);

  const handleCreateStaff = async (e) => {
    e.preventDefault();
    if (!newStaffData.username || !newStaffData.email || !newStaffData.password || !newStaffData.role) {
      if (showNotification) showNotification('error', 'Validation Error', 'Please fill in all fields');
      return;
    }
    
    setIsCreatingStaff(true);
    try {
      const response = await subUserService.createSubUser(newStaffData);
      if (response && response.success) {
        if (showNotification) showNotification('success', 'Staff Created', 'Staff member added successfully');
        setShowAddStaffModal(false);
        setNewStaffData({ username: '', email: '', password: '', role: 'TEACHER' });
        // Refresh users list
        const resUsers = await subUserService.getAllSubUsers();
        if (resUsers && resUsers.success && Array.isArray(resUsers.users)) {
          setUsers(resUsers.users);
        }
      } else {
        throw new Error(response.error || 'Failed to create staff');
      }
    } catch (err) {
      console.error('Error creating staff:', err);
      if (showNotification) showNotification('error', 'Error', err.message || 'Failed to create staff user');
    } finally {
      setIsCreatingStaff(false);
    }
  };

  // 7. Payment State
  const [paymentDetails, setPaymentDetails] = useState({
    upiId: '',
    bankAccountNumber: '',
    bankName: '',
    bankIfscCode: '',
    bankAccountHolderName: orgData?.orgName || orgData?.institutionName || 'School Management',
    acceptUpi: true,
    acceptBankTransfer: true
  });

  // Load Branding & Configs on Mount
  useEffect(() => {
    loadAllSettings();
  }, []);

  const loadAllSettings = async () => {
    // Fetch Profile details from DB
    try {
      const res = await authService.getAdminProfile();
      if (res && res.orgId) {
        setProfileForm({
          trustName: res.trustName || '',
          institutionName: res.orgName || '',
          orgId: res.orgId || '',
          address: res.address || '',
          phone: res.phoneNumber || '',
          email: res.email || '',
          website: res.website || '',
          affiliation: res.affiliation || '',
          principalName: res.principalName || '',
          allowParentPhotoUpload: res.allowParentPhotoUpload !== undefined ? res.allowParentPhotoUpload : true,
          allowTeacherPhotoUpload: res.allowTeacherPhotoUpload !== undefined ? res.allowTeacherPhotoUpload : true
        });
        if (res.trustName) {
          localStorage.setItem('trustName', res.trustName);
          setBranding(prev => ({ ...prev, trustName: res.trustName }));
        }
      }
    } catch (err) {
      console.error('Error loading profile:', err);
    }

    // Fetch Branding
    try {
      setBrandingLoading(true);
      const res = await brandingService.getBranding();
      if (res && res.success && res.branding) {
        const fetchedTrust = res.branding.trustName || '';
        setBranding({
          trustName: fetchedTrust,
          logoUrl: res.branding.logoUrl || '',
          signatureUrl: res.branding.signatureUrl || '',
          sealUrl: res.branding.sealUrl || '',
          primaryColor: res.branding.primaryColor || '#1e3a8a',
          secondaryColor: res.branding.secondaryColor || '#f59e0b',
          academicYear: res.branding.academicYear || '2026-27'
        });
        if (fetchedTrust) {
          localStorage.setItem('trustName', fetchedTrust);
          setProfileForm(prev => ({ ...prev, trustName: fetchedTrust }));
        }
      }
    } catch (err) {
      console.error('Error loading branding:', err);
    } finally {
      setBrandingLoading(false);
    }

    // Fetch Sub Users
    try {
      setUsersLoading(true);
      const resUsers = await subUserService.getAllSubUsers();
      if (resUsers && resUsers.success && Array.isArray(resUsers.users)) {
        setUsers(resUsers.users);
      }
    } catch (err) {
      console.error('Error loading staff users:', err);
    } finally {
      setUsersLoading(false);
    }

    // Fetch Sequence Configs
    try {
      const res = await identifierService.getConfigs();
      if (res && res.success && Array.isArray(res.configs)) {
        setDbConfigs(res.configs);
        
        const student = res.configs.find(c => c.sequenceType === 'STUDENT');
        if (student) {
          setStudentIdConfig({
            mode: student.enabled ? 'AUTOMATIC' : 'MANUAL',
            prefix: student.prefix || 'SCH',
            includeYear: student.pattern.includes('{YEAR}'),
            yearFormat: 'YYYY',
            separator: student.pattern.replace('{PREFIX}', '').replace('{YEAR}', '').replace('{SEQ}', '').substring(0, 1) || '-',
            padding: student.padding || 4,
            startSeq: 1,
            resetPolicy: student.resetPolicy || 'ACADEMIC_YEAR',
            nextSeq: student.nextVal || 1
          });
        }
        
        const teacher = res.configs.find(c => c.sequenceType === 'TEACHER');
        if (teacher) {
          setTeacherIdConfig({
            mode: teacher.enabled ? 'AUTOMATIC' : 'MANUAL',
            prefix: teacher.prefix || 'TCH',
            includeYear: teacher.pattern.includes('{YEAR}'),
            separator: teacher.pattern.replace('{PREFIX}', '').replace('{YEAR}', '').replace('{SEQ}', '').substring(0, 1) || '-',
            padding: teacher.padding || 3,
            startSeq: 101,
            resetPolicy: teacher.resetPolicy || 'NEVER',
            nextSeq: teacher.nextVal || 1
          });
        }

        const library = res.configs.find(c => c.sequenceType === 'LIBRARY_MEMBER');
        if (library) {
          setLibraryIdConfig({
            mode: library.enabled ? 'AUTOMATIC' : 'MANUAL',
            prefix: library.prefix || 'LIB',
            includeYear: library.pattern.includes('{YEAR}'),
            separator: library.pattern.replace('{PREFIX}', '').replace('{YEAR}', '').replace('{SEQ}', '').substring(0, 1) || '-',
            padding: library.padding || 4,
            startSeq: 1,
            resetPolicy: library.resetPolicy || 'NEVER',
            nextSeq: library.nextVal || 1
          });
        }

        const receipt = res.configs.find(c => c.sequenceType === 'FEE_RECEIPT');
        if (receipt) {
          setReceiptIdConfig({
            mode: receipt.enabled ? 'AUTOMATIC' : 'MANUAL',
            prefix: receipt.prefix || 'RCPT',
            includeYear: receipt.pattern.includes('{YEAR}'),
            separator: receipt.pattern.replace('{PREFIX}', '').replace('{YEAR}', '').replace('{SEQ}', '').substring(0, 1) || '-',
            padding: receipt.padding || 4,
            startSeq: 1,
            resetPolicy: receipt.resetPolicy || 'ACADEMIC_YEAR',
            nextSeq: receipt.nextVal || 1
          });
        }
      }
    } catch (err) {
      console.error('Error loading identifier settings:', err);
    }

    // Restore Saved Academic & Progression Settings
    try {
      const orgId = orgData?.orgId || 'SCH-02';
      const savedAcademic = localStorage.getItem(`academic_settings_${orgId}`);
      if (savedAcademic) {
        const parsed = JSON.parse(savedAcademic);
        if (parsed.academicConfig) setAcademicConfig(parsed.academicConfig);
        if (parsed.academicYearsList && Array.isArray(parsed.academicYearsList)) setAcademicYearsList(parsed.academicYearsList);
        if (parsed.classProgressionRules && Array.isArray(parsed.classProgressionRules)) setClassProgressionRules(parsed.classProgressionRules);
      }
    } catch (err) {
      console.error('Error restoring academic settings:', err);
    }

    // Restore Saved Roll Number Settings
    try {
      // First try to fetch from backend
      const response = await identifierService.getRollNumberSettings('2026-27');
      if (response && response.success && response.settings) {
        setRollNoConfig({
          rollNumberMode: response.settings.rollNumberMode || 'SEQUENTIAL_PER_CLASS',
          assignmentOrdering: response.settings.assignmentOrdering || 'ALPHABETICAL',
          startingRollNumber: response.settings.startingRollNumber || 1,
          padding: response.settings.padding || 3,
          prefix: response.settings.prefix || '',
          suffix: response.settings.suffix || '',
          separator: response.settings.separator || '',
          resetPolicy: response.settings.resetPolicy || 'ACADEMIC_YEAR',
          regenerateOnPromotion: response.settings.regenerateOnPromotion !== false,
          allowManualOverride: response.settings.allowManualOverride !== false,
          preview: response.preview || ''
        });
      } else {
        // Fallback to localStorage for backward compatibility if backend fails
        const orgId = orgData?.orgId || 'SCH-02';
        const savedRollNo = localStorage.getItem(`roll_number_settings_${orgId}`);
        if (savedRollNo) {
          setRollNoConfig(JSON.parse(savedRollNo));
        }
      }
    } catch (err) {
      console.error('Error fetching roll number settings:', err);
    }
  };

  // Helper full URL resolver
  const resolveAssetUrl = (path) => {
    if (!path || typeof path !== 'string' || path.trim() === '') return '';
    if (path.startsWith('http://') || path.startsWith('https://') || path.startsWith('data:') || path.startsWith('blob:')) return path;
    const cleanPath = path.startsWith('/') ? path : `/${path}`;
    const baseUrl = API_BASE_URL || 'http://localhost:9980';
    return `${baseUrl}${cleanPath}`;
  };

  // Upload Branding Asset
  const handleUploadBrandingAsset = async (type, e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      if (showNotification) showNotification('error', 'File Too Large', 'Asset image must be less than 5MB');
      return;
    }

    setUploadingAsset(type);
    try {
      const res = await brandingService.uploadAsset(type, file);
      if (res && res.success) {
        const relativeUrl = res.url || res.path || '';
        setBranding(prev => ({
          ...prev,
          [`${type}Url`]: relativeUrl
        }));
        if (showNotification) showNotification('success', 'Asset Uploaded', `${type.toUpperCase()} uploaded successfully`);
      }
    } catch (err) {
      console.error('Error uploading asset:', err);
      if (showNotification) showNotification('error', 'Upload Failed', 'Failed to upload asset file');
    } finally {
      setUploadingAsset('');
    }
  };

  // Remove Branding Asset
  const handleRemoveAsset = (type) => {
    setBranding(prev => ({ ...prev, [`${type}Url`]: '' }));
    if (showNotification) showNotification('success', 'Asset Removed', `${type.toUpperCase()} removed`);
  };

  // Save Profile Settings (Secure PUT to backend)
  const handleSaveProfile = async (e) => {
    if (e) e.preventDefault();

    // Client-side validations
    if (!profileForm.institutionName || !profileForm.institutionName.trim()) {
      if (showNotification) showNotification('error', 'Validation Error', 'Institution name is required');
      return;
    }
    if (!profileForm.email || !profileForm.email.trim()) {
      if (showNotification) showNotification('error', 'Validation Error', 'Email is required');
      return;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(profileForm.email.trim())) {
      if (showNotification) showNotification('error', 'Validation Error', 'Invalid email format');
      return;
    }
    if (profileForm.phone && (profileForm.phone.trim().length < 10 || profileForm.phone.trim().length > 15)) {
      if (showNotification) showNotification('error', 'Validation Error', 'Phone number must be between 10 and 15 characters');
      return;
    }

    // Confirmation before changing institution type
    const currentType = getNormalizedInstitutionType(orgData);
    if (profileForm.institutionType !== currentType) {
      const confirmed = window.confirm(
        "Changing the institution type changes terminology, dashboard and available modules for this organization. Existing student, fee, attendance and historical data will not be deleted.\n\nDo you want to proceed?"
      );
      if (!confirmed) return;
    }

    try {
      setProfileSaving(true);
      const updatePayload = {
        trustName: profileForm.trustName ? profileForm.trustName.trim() : '',
        institutionName: profileForm.institutionName.trim(),
        institutionType: profileForm.institutionType,
        email: profileForm.email.trim(),
        phoneNumber: profileForm.phone ? profileForm.phone.trim() : '',
        address: profileForm.address ? profileForm.address.trim() : '',
        affiliation: profileForm.affiliation ? profileForm.affiliation.trim() : '',
        principalName: profileForm.principalName ? profileForm.principalName.trim() : '',
        allowParentPhotoUpload: profileForm.allowParentPhotoUpload !== false,
        allowTeacherPhotoUpload: profileForm.allowTeacherPhotoUpload !== false,
        city: orgData?.city || '',
        state: orgData?.state || '',
        pincode: orgData?.pincode || ''
      };

      const res = await authService.updateAdminProfile(updatePayload);
      if (res && res.success && res.organization) {
        const updated = res.organization;
        const updatedOrgData = {
          ...orgData,
          trustName: updated.trustName !== undefined ? updated.trustName : (profileForm.trustName || ''),
          orgName: updated.orgName,
          institutionType: updated.institutionType || profileForm.institutionType,
          email: updated.email,
          phoneNumber: updated.phoneNumber,
          address: updated.address,
          principalName: updated.principalName,
          affiliation: updated.affiliation,
          allowParentPhotoUpload: updated.allowParentPhotoUpload !== undefined ? updated.allowParentPhotoUpload : profileForm.allowParentPhotoUpload,
          allowTeacherPhotoUpload: updated.allowTeacherPhotoUpload !== undefined ? updated.allowTeacherPhotoUpload : profileForm.allowTeacherPhotoUpload
        };
        localStorage.setItem('orgData', JSON.stringify(updatedOrgData));
        if (profileForm.trustName) {
          localStorage.setItem('trustName', profileForm.trustName.trim());
        } else {
          localStorage.removeItem('trustName');
        }
        window.dispatchEvent(new Event('orgDataChanged'));

        if (showNotification) {
          showNotification('success', 'Saved Successfully', 'Institution profile and trust details updated');
        }
      } else {
        throw new Error(res.message || 'Failed to update profile');
      }
    } catch (err) {
      console.error('Save profile error:', err);
      if (showNotification) {
        showNotification('error', 'Save Failed', err.message || 'Failed to save institution profile');
      }
    } finally {
      setProfileSaving(false);
    }
  };

  // Save Branding Settings
  const handleSaveBranding = async () => {
    try {
      setBrandingLoading(true);
      await brandingService.saveBranding(branding);
      if (branding.trustName !== undefined) {
        const currentOrg = JSON.parse(localStorage.getItem('orgData') || '{}');
        currentOrg.trustName = branding.trustName;
        localStorage.setItem('orgData', JSON.stringify(currentOrg));
        if (branding.trustName) {
          localStorage.setItem('trustName', branding.trustName.trim());
        } else {
          localStorage.removeItem('trustName');
        }
        window.dispatchEvent(new Event('orgDataChanged'));
      }
      if (showNotification) showNotification('success', 'Branding Saved', 'Centralized institution branding updated successfully');
    } catch (err) {
      if (showNotification) showNotification('error', 'Error', 'Failed to save branding settings');
    } finally {
      setBrandingLoading(false);
    }
  };

  // Save All Sequence Configurations (Student, Teacher, Library, Receipts)
  const handleSaveIdSettings = async () => {
    setStudentIdSaving(true);
    try {
      // 1. Build and save Student ID config
      const studentPattern = studentIdConfig.includeYear 
        ? `{PREFIX}${studentIdConfig.separator}{YEAR}${studentIdConfig.separator}{SEQ}`
        : `{PREFIX}${studentIdConfig.separator}{SEQ}`;
      const studentPayload = {
        sequenceType: 'STUDENT',
        prefix: studentIdConfig.prefix,
        padding: studentIdConfig.padding,
        pattern: studentPattern,
        resetPolicy: studentIdConfig.resetPolicy,
        enabled: studentIdConfig.mode === 'AUTOMATIC'
      };

      // 2. Build and save Teacher ID config
      const teacherPattern = teacherIdConfig.includeYear 
        ? `{PREFIX}${teacherIdConfig.separator}{YEAR}${teacherIdConfig.separator}{SEQ}`
        : `{PREFIX}${teacherIdConfig.separator}{SEQ}`;
      const teacherPayload = {
        sequenceType: 'TEACHER',
        prefix: teacherIdConfig.prefix,
        padding: teacherIdConfig.padding,
        pattern: teacherPattern,
        resetPolicy: teacherIdConfig.resetPolicy,
        enabled: teacherIdConfig.mode === 'AUTOMATIC'
      };

      // 3. Build and save Library ID config
      const libraryPattern = libraryIdConfig.includeYear 
        ? `{PREFIX}${libraryIdConfig.separator}{YEAR}${libraryIdConfig.separator}{SEQ}`
        : `{PREFIX}${libraryIdConfig.separator}{SEQ}`;
      const libraryPayload = {
        sequenceType: 'LIBRARY_MEMBER',
        prefix: libraryIdConfig.prefix,
        padding: libraryIdConfig.padding,
        pattern: libraryPattern,
        resetPolicy: libraryIdConfig.resetPolicy,
        enabled: libraryIdConfig.mode === 'AUTOMATIC'
      };

      // 4. Build and save Receipt ID config
      const receiptPattern = receiptIdConfig.includeYear 
        ? `{PREFIX}${receiptIdConfig.separator}{YEAR}${receiptIdConfig.separator}{SEQ}`
        : `{PREFIX}${receiptIdConfig.separator}{SEQ}`;
      const receiptPayload = {
        sequenceType: 'FEE_RECEIPT',
        prefix: receiptIdConfig.prefix,
        padding: receiptIdConfig.padding,
        pattern: receiptPattern,
        resetPolicy: receiptIdConfig.resetPolicy,
        enabled: receiptIdConfig.mode === 'AUTOMATIC'
      };

      await identifierService.saveConfig(studentPayload);
      await identifierService.saveConfig(teacherPayload);
      await identifierService.saveConfig(libraryPayload);
      await identifierService.saveConfig(receiptPayload);

      await loadAllSettings();
      if (showNotification) showNotification('success', 'Settings Saved', 'ID sequence configurations saved successfully');
    } catch (err) {
      console.error('Error saving configs:', err);
      if (showNotification) showNotification('error', 'Save Failed', 'Failed to save configuration settings');
    } finally {
      setStudentIdSaving(false);
    }
  };

  // Open Reset Dialog
  const triggerResetSequence = (type) => {
    setResetType(type);
    setResetReason('');
    setResetNextVal(1);
    setResetForce(false);
    setResetWarning('');
    
    // Check if user is SUPER_ADMIN
    const userRole = localStorage.getItem('userRole') || '';
    const isSuper = userRole.toUpperCase() === 'SUPER_ADMIN';
    setCanForce(isSuper);
    
    setResetModalOpen(true);
  };

  // Submit Reset Sequence Request
  const handleResetSubmit = async (e) => {
    e.preventDefault();
    if (!resetReason.trim()) {
      if (showNotification) showNotification('error', 'Reason Required', 'Please enter a reason for resetting this sequence.');
      return;
    }

    setResetLoading(true);
    try {
      const payload = {
        sequenceType: resetType,
        nextVal: resetNextVal,
        reason: resetReason,
        force: resetForce
      };

      const res = await identifierService.resetSequence(payload);
      if (res && res.success) {
        if (showNotification) showNotification('success', 'Sequence Reset', `Sequence for ${resetType} reset successfully.`);
        setResetModalOpen(false);
        await loadAllSettings();
      } else if (res && res.warning) {
        setResetWarning(res.warning);
        // Override with back-end check if user role from server allows force reset
        if (res.canForce !== undefined) {
          setCanForce(res.canForce);
        }
      } else {
        if (showNotification) showNotification('error', 'Reset Rejected', res.error || 'Failed to reset sequence');
      }
    } catch (err) {
      console.error('Error resetting sequence:', err);
      if (showNotification) showNotification('error', 'Reset Failed', err.message || 'Server error during sequence reset');
    } finally {
      setResetLoading(false);
    }
  };

  // Student ID Live Preview Calculation
  const studentIdLivePreview = useMemo(() => {
    const year = studentIdConfig.includeYear ? '2026' : '';
    const seqPadded = String(1).padStart(studentIdConfig.padding, '0');
    const sep = studentIdConfig.separator;
    
    let parts = [studentIdConfig.prefix];
    if (year) parts.push(year);
    parts.push(seqPadded);
    return parts.filter(Boolean).join(sep);
  }, [studentIdConfig]);

  const studentIdNextPreview = useMemo(() => {
    const year = studentIdConfig.includeYear ? '2026' : '';
    const seqPadded = String(studentIdConfig.nextSeq).padStart(studentIdConfig.padding, '0');
    const sep = studentIdConfig.separator;
    
    let parts = [studentIdConfig.prefix];
    if (year) parts.push(year);
    parts.push(seqPadded);
    return parts.filter(Boolean).join(sep);
  }, [studentIdConfig]);

  // Teacher ID Live Preview Calculation
  const teacherIdLivePreview = useMemo(() => {
    const year = teacherIdConfig.includeYear ? '2026' : '';
    const seqPadded = String(teacherIdConfig.startSeq || 101).padStart(teacherIdConfig.padding, '0');
    const sep = teacherIdConfig.separator;

    let parts = [teacherIdConfig.prefix];
    if (year) parts.push(year);
    parts.push(seqPadded);
    return parts.filter(Boolean).join(sep);
  }, [teacherIdConfig]);

  const teacherIdNextPreview = useMemo(() => {
    const year = teacherIdConfig.includeYear ? '2026' : '';
    const seqPadded = String(teacherIdConfig.nextSeq || 107).padStart(teacherIdConfig.padding, '0');
    const sep = teacherIdConfig.separator;

    let parts = [teacherIdConfig.prefix];
    if (year) parts.push(year);
    parts.push(seqPadded);
    return parts.filter(Boolean).join(sep);
  }, [teacherIdConfig]);

  // Library ID Live Preview Calculation
  const libraryIdLivePreview = useMemo(() => {
    const year = libraryIdConfig.includeYear ? '2026' : '';
    const seqPadded = String(1).padStart(libraryIdConfig.padding, '0');
    const sep = libraryIdConfig.separator;
    
    let parts = [libraryIdConfig.prefix];
    if (year) parts.push(year);
    parts.push(seqPadded);
    return parts.filter(Boolean).join(sep);
  }, [libraryIdConfig]);

  const libraryIdNextPreview = useMemo(() => {
    const year = libraryIdConfig.includeYear ? '2026' : '';
    const seqPadded = String(libraryIdConfig.nextSeq).padStart(libraryIdConfig.padding, '0');
    const sep = libraryIdConfig.separator;
    
    let parts = [libraryIdConfig.prefix];
    if (year) parts.push(year);
    parts.push(seqPadded);
    return parts.filter(Boolean).join(sep);
  }, [libraryIdConfig]);

  // Fee Receipt ID Live Preview Calculation
  const receiptIdLivePreview = useMemo(() => {
    const year = receiptIdConfig.includeYear ? '2026' : '';
    const seqPadded = String(1).padStart(receiptIdConfig.padding, '0');
    const sep = receiptIdConfig.separator;
    
    let parts = [receiptIdConfig.prefix];
    if (year) parts.push(year);
    parts.push(seqPadded);
    return parts.filter(Boolean).join(sep);
  }, [receiptIdConfig]);

  const receiptIdNextPreview = useMemo(() => {
    const year = receiptIdConfig.includeYear ? '2026' : '';
    const seqPadded = String(receiptIdConfig.nextSeq).padStart(receiptIdConfig.padding, '0');
    const sep = receiptIdConfig.separator;
    
    let parts = [receiptIdConfig.prefix];
    if (year) parts.push(year);
    parts.push(seqPadded);
    return parts.filter(Boolean).join(sep);
  }, [receiptIdConfig]);

  return (
    <div style={{ background: '#f8fafc', minHeight: '100vh', padding: '20px', fontFamily: '"Inter", sans-serif' }}>
      
      {/* ── Settings Header ── */}
      <div style={{ background: '#ffffff', padding: '20px 24px', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', border: '1px solid #e2e8f0', marginBottom: '20px' }}>
        <h1 style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '22px', fontWeight: '800', color: '#0f172a', margin: 0 }}>
          <Building size={24} color="#2563eb" /> Centralized Institution Configuration Control Center
        </h1>
        <p style={{ color: '#64748b', fontSize: '13px', margin: '4px 0 0 34px' }}>
          Configure institution profiles, branding, automatic Student ID generation, roll numbers, academic rules, staff permissions, and security.
        </p>
      </div>

      {/* ── Horizontal Navigation Tabs Bar ── */}
      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '20px', background: '#ffffff', padding: '8px', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
        <button 
          onClick={() => setActiveSubTab('profile')}
          style={{ padding: '9px 16px', borderRadius: '8px', border: 'none', background: activeSubTab === 'profile' ? '#2563eb' : 'transparent', color: activeSubTab === 'profile' ? '#fff' : '#64748b', fontSize: '13px', fontWeight: '600', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px' }}
        >
          <Building size={15} /> Institution Profile
        </button>

        <button 
          onClick={() => setActiveSubTab('branding')}
          style={{ padding: '9px 16px', borderRadius: '8px', border: 'none', background: activeSubTab === 'branding' ? '#2563eb' : 'transparent', color: activeSubTab === 'branding' ? '#fff' : '#64748b', fontSize: '13px', fontWeight: '600', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px' }}
        >
          <Image size={15} /> Branding & Documents
        </button>

        <button 
          onClick={() => setActiveSubTab('student_id')}
          style={{ padding: '9px 16px', borderRadius: '8px', border: 'none', background: activeSubTab === 'student_id' ? '#2563eb' : 'transparent', color: activeSubTab === 'student_id' ? '#fff' : '#64748b', fontSize: '13px', fontWeight: '600', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px' }}
        >
          <Hash size={15} /> Student ID & Numbering
        </button>

        <button 
          onClick={() => setActiveSubTab('roll_no')}
          style={{ padding: '9px 16px', borderRadius: '8px', border: 'none', background: activeSubTab === 'roll_no' ? '#2563eb' : 'transparent', color: activeSubTab === 'roll_no' ? '#fff' : '#64748b', fontSize: '13px', fontWeight: '600', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px' }}
        >
          <Hash size={15} /> Roll Number Settings
        </button>

        <button 
          onClick={() => setActiveSubTab('academic')}
          style={{ padding: '9px 16px', borderRadius: '8px', border: 'none', background: activeSubTab === 'academic' ? '#2563eb' : 'transparent', color: activeSubTab === 'academic' ? '#fff' : '#64748b', fontSize: '13px', fontWeight: '600', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px' }}
        >
          <BookOpen size={15} /> Academic Settings
        </button>

        <button 
          onClick={() => setActiveSubTab('report_card_templates')}
          style={{ padding: '9px 16px', borderRadius: '8px', border: 'none', background: activeSubTab === 'report_card_templates' ? '#2563eb' : 'transparent', color: activeSubTab === 'report_card_templates' ? '#fff' : '#64748b', fontSize: '13px', fontWeight: '600', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px' }}
        >
          <FileText size={15} /> Report Card Templates
        </button>

        <button 
          onClick={() => setActiveSubTab('library')}
          style={{ padding: '9px 16px', borderRadius: '8px', border: 'none', background: activeSubTab === 'library' ? '#2563eb' : 'transparent', color: activeSubTab === 'library' ? '#fff' : '#64748b', fontSize: '13px', fontWeight: '600', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px' }}
        >
          <BookOpen size={15} /> Library Rules
        </button>

        <button 
          onClick={() => setActiveSubTab('users')}
          style={{ padding: '9px 16px', borderRadius: '8px', border: 'none', background: activeSubTab === 'users' ? '#2563eb' : 'transparent', color: activeSubTab === 'users' ? '#fff' : '#64748b', fontSize: '13px', fontWeight: '600', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px' }}
        >
          <Users size={15} /> Staff Users ({users.length})
        </button>

        <button 
          onClick={() => setActiveSubTab('permissions')}
          style={{ padding: '9px 16px', borderRadius: '8px', border: 'none', background: activeSubTab === 'permissions' ? '#2563eb' : 'transparent', color: activeSubTab === 'permissions' ? '#fff' : '#64748b', fontSize: '13px', fontWeight: '600', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px' }}
        >
          <Shield size={15} /> Permissions
        </button>

        <button 
          onClick={() => setActiveSubTab('payment')}
          style={{ padding: '9px 16px', borderRadius: '8px', border: 'none', background: activeSubTab === 'payment' ? '#2563eb' : 'transparent', color: activeSubTab === 'payment' ? '#fff' : '#64748b', fontSize: '13px', fontWeight: '600', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px' }}
        >
          <CreditCard size={15} /> Payment & Bank
        </button>

        <button 
          onClick={() => setActiveSubTab('security')}
          style={{ padding: '9px 16px', borderRadius: '8px', border: 'none', background: activeSubTab === 'security' ? '#2563eb' : 'transparent', color: activeSubTab === 'security' ? '#fff' : '#64748b', fontSize: '13px', fontWeight: '600', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px' }}
        >
          <Lock size={15} /> Student & Parent Login
        </button>

        <button 
          onClick={() => setActiveSubTab('whatsapp')}
          style={{ padding: '9px 16px', borderRadius: '8px', border: 'none', background: activeSubTab === 'whatsapp' ? '#2563eb' : 'transparent', color: activeSubTab === 'whatsapp' ? '#fff' : '#64748b', fontSize: '13px', fontWeight: '600', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px' }}
        >
          <MessageSquare size={15} /> WhatsApp & SMS Gateway
        </button>

        <button 
          onClick={() => setActiveSubTab('storage')}
          style={{ padding: '9px 16px', borderRadius: '8px', border: 'none', background: activeSubTab === 'storage' ? '#2563eb' : 'transparent', color: activeSubTab === 'storage' ? '#fff' : '#64748b', fontSize: '13px', fontWeight: '600', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px' }}
        >
          <HardDrive size={15} /> Media & Storage Quota
        </button>
      </div>

      {/* ── TAB: WHATSAPP & SMS GATEWAY ── */}
      {activeSubTab === 'whatsapp' && (
        <div style={{ background: '#ffffff', borderRadius: '12px', border: '1px solid #e2e8f0', padding: '24px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px', flexWrap: 'wrap', gap: '12px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div style={{ width: '42px', height: '42px', borderRadius: '10px', background: '#dcfce7', color: '#16a34a', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '22px' }}>
                📱
              </div>
              <div>
                <h3 style={{ fontSize: '17px', fontWeight: '800', color: '#0f172a', margin: 0 }}>
                  Centralized WhatsApp API Gateway & Sub-User Configuration
                </h3>
                <p style={{ color: '#64748b', fontSize: '13px', margin: '4px 0 0 0' }}>
                  Connect your custom WhatsApp API endpoint and sub-user instance to send instant automated absent alerts to parents.
                </p>
              </div>
            </div>

            <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: '700', color: '#0f172a', background: '#f8fafc', padding: '6px 12px', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                <input
                  type="checkbox"
                  checked={whatsappConfig.enableWhatsapp}
                  onChange={e => setWhatsappConfig({ ...whatsappConfig, enableWhatsapp: e.target.checked })}
                  style={{ accentColor: '#16a34a' }}
                />
                <span>Enable WhatsApp Alerts</span>
              </label>
            </div>
          </div>

          <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '20px', marginBottom: '20px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '18px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: '700', color: '#475569', marginBottom: '6px' }}>GATEWAY PROVIDER</label>
                <select
                  value={whatsappConfig.whatsappProvider || 'CUSTOM_GATEWAY'}
                  onChange={e => setWhatsappConfig({ ...whatsappConfig, whatsappProvider: e.target.value })}
                  style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '13.5px', fontWeight: '600' }}
                >
                  <option value="CUSTOM_GATEWAY">Custom WhatsApp Gateway / Webhook (Recommended)</option>
                  <option value="ULTRAMSG">UltraMsg API Gateway</option>
                  <option value="WATI">WATI WhatsApp Business API</option>
                  <option value="INTERAKT">Interakt WhatsApp API</option>
                  <option value="GENERIC_REST">Generic REST HTTP Gateway</option>
                </select>
                <span style={{ fontSize: '11px', color: '#64748b', marginTop: '4px', display: 'block' }}>Choose your WhatsApp provider or custom API type</span>
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: '700', color: '#475569', marginBottom: '6px' }}>WHATSAPP API ENDPOINT URL</label>
                <input
                  type="text"
                  placeholder="https://api.yourgateway.com/send-message"
                  value={whatsappConfig.whatsappApiUrl || ''}
                  onChange={e => setWhatsappConfig({ ...whatsappConfig, whatsappApiUrl: e.target.value })}
                  style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '13.5px' }}
                />
                <span style={{ fontSize: '11px', color: '#64748b', marginTop: '4px', display: 'block' }}>HTTP POST endpoint where messages are routed</span>
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: '700', color: '#475569', marginBottom: '6px' }}>API KEY / AUTH TOKEN</label>
                <input
                  type="password"
                  placeholder="Paste your API key or Bearer Token..."
                  value={whatsappConfig.whatsappApiKey || ''}
                  onChange={e => setWhatsappConfig({ ...whatsappConfig, whatsappApiKey: e.target.value })}
                  style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '13.5px' }}
                />
                <span style={{ fontSize: '11px', color: '#64748b', marginTop: '4px', display: 'block' }}>Authorization secret for API authentication</span>
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: '700', color: '#475569', marginBottom: '6px' }}>SCHOOL SUB-USER / INSTANCE ID</label>
                <input
                  type="text"
                  placeholder="e.g. subuser_sch02 or instance_998"
                  value={whatsappConfig.whatsappSubUser || ''}
                  onChange={e => setWhatsappConfig({ ...whatsappConfig, whatsappSubUser: e.target.value })}
                  style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '13.5px', fontWeight: '600' }}
                />
                <span style={{ fontSize: '11px', color: '#64748b', marginTop: '4px', display: 'block' }}>Assigned sub-user or instance account for this school</span>
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: '700', color: '#475569', marginBottom: '6px' }}>SENDER PHONE (OPTIONAL)</label>
                <input
                  type="text"
                  placeholder="+919876543210"
                  value={whatsappConfig.whatsappSenderNumber || ''}
                  onChange={e => setWhatsappConfig({ ...whatsappConfig, whatsappSenderNumber: e.target.value })}
                  style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '13.5px' }}
                />
                <span style={{ fontSize: '11px', color: '#64748b', marginTop: '4px', display: 'block' }}>Sender channel ID / phone if required by gateway</span>
              </div>
            </div>

            {/* Template Section */}
            <div style={{ marginTop: '18px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px', flexWrap: 'wrap', gap: '8px' }}>
                <label style={{ fontSize: '12.5px', fontWeight: '700', color: '#475569' }}>CUSTOM PARENT ABSENT MESSAGE TEMPLATE</label>
                <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                  {['{{studentName}}', '{{className}}', '{{section}}', '{{rollNo}}', '{{date}}', '{{schoolName}}'].map(tag => (
                    <button
                      key={tag}
                      type="button"
                      onClick={() => setWhatsappConfig({ ...whatsappConfig, whatsappAbsentTemplate: (whatsappConfig.whatsappAbsentTemplate || '') + ' ' + tag })}
                      style={{ padding: '3px 8px', borderRadius: '4px', border: '1px solid #cbd5e1', background: '#ffffff', fontSize: '11px', fontWeight: '600', color: '#2563eb', cursor: 'pointer' }}
                    >
                      + {tag}
                    </button>
                  ))}
                </div>
              </div>
              <textarea
                rows="3"
                value={whatsappConfig.whatsappAbsentTemplate || ''}
                onChange={e => setWhatsappConfig({ ...whatsappConfig, whatsappAbsentTemplate: e.target.value })}
                style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '13px', fontFamily: 'monospace' }}
              />
            </div>

            {/* Live Test Box */}
            <div style={{ marginTop: '18px', borderTop: '1px dashed #cbd5e1', paddingTop: '16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <input
                  type="text"
                  placeholder="Enter Admin Mobile (e.g. 9876543210)..."
                  value={waTestPhone}
                  onChange={e => setWaTestPhone(e.target.value)}
                  style={{ padding: '9px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '13px', width: '250px' }}
                />
                <button
                  type="button"
                  onClick={handleTestWhatsAppConnection}
                  disabled={waTestLoading}
                  style={{ padding: '9px 18px', background: '#16a34a', color: '#ffffff', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: '700', cursor: waTestLoading ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', gap: '6px' }}
                >
                  <Smartphone size={15} /> {waTestLoading ? 'Sending Test Alert...' : 'Send Test WhatsApp Alert'}
                </button>
              </div>

              {waTestResult && (
                <div style={{ padding: '8px 16px', borderRadius: '8px', background: waTestResult.success ? '#dcfce7' : '#fee2e2', color: waTestResult.success ? '#15803d' : '#b91c1c', fontSize: '12.5px', fontWeight: '700' }}>
                  {waTestResult.success ? '✅ Test Message Dispatched Successfully!' : `❌ Test Failed: ${waTestResult.error || 'Check connection details'}`}
                </div>
              )}
            </div>
          </div>

          <button
            type="button"
            disabled={whatsappSaving}
            onClick={handleSaveWhatsApp}
            style={{ padding: '10px 24px', background: '#2563eb', color: '#ffffff', border: 'none', borderRadius: '8px', fontSize: '13.5px', fontWeight: '700', cursor: whatsappSaving ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', gap: '8px', boxShadow: '0 4px 12px rgba(37, 99, 235, 0.3)' }}
          >
            <Save size={16} /> {whatsappSaving ? 'Saving Settings...' : 'Save WhatsApp & SMS Gateway Settings'}
          </button>
        </div>
      )}

      {/* ── TAB: STUDENT & PARENT LOGIN CREDENTIALS ── */}
      {activeSubTab === 'security' && (
        <div style={{ background: '#ffffff', borderRadius: '12px', border: '1px solid #e2e8f0', padding: '24px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
            <span style={{ fontSize: '24px' }}>🔑</span>
            <div>
              <h3 style={{ fontSize: '17px', fontWeight: '700', color: '#0f172a', margin: 0 }}>
                Student & Parent App Login Password Management
              </h3>
              <p style={{ color: '#64748b', fontSize: '13px', margin: '4px 0 0 0' }}>
                Set the default password for new admissions or bulk-reset login credentials for all registered students and parents.
              </p>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '20px', marginTop: '20px' }}>
            {/* Card 1: Default Password Policy */}
            <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '20px' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ fontSize: '18px' }}>⚙️</span>
                  <h4 style={{ margin: 0, fontSize: '15px', color: '#1e293b', fontWeight: '700' }}>Standard Default Password</h4>
                </div>
                <span style={{ fontSize: '11px', fontWeight: '700', padding: '2px 8px', borderRadius: '12px', background: '#eff6ff', color: '#2563eb', border: '1px solid #bfdbfe' }}>
                  Active for New Admissions
                </span>
              </div>
              <p style={{ fontSize: '13px', color: '#64748b', lineHeight: 1.5, marginBottom: '16px' }}>
                Whenever a new student admission is added, this password will be assigned automatically if no custom password is typed.
              </p>

              <div style={{ marginBottom: '16px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                  <label style={{ fontSize: '12.5px', fontWeight: '600', color: '#475569' }}>Default Initial Password</label>
                  {defaultStudentPassword && (
                    <span style={{ fontSize: '11.5px', color: '#059669', fontWeight: '600' }}>
                      Active: {defaultStudentPassword}
                    </span>
                  )}
                </div>
                <input 
                  type="text" 
                  value={defaultStudentPassword} 
                  onChange={e => setDefaultStudentPassword(e.target.value)} 
                  placeholder="e.g. Student@123"
                  style={{ width: '100%', padding: '10px 12px', border: '1px solid #cbd5e1', borderRadius: '8px', fontSize: '13.5px', fontWeight: '600', color: '#0f172a' }} 
                />
                <label style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', marginTop: '10px', cursor: 'pointer', fontSize: '12.5px', color: '#334155', lineHeight: 1.4 }}>
                  <input 
                    type="checkbox" 
                    checked={alsoApplyToExisting} 
                    onChange={e => setAlsoApplyToExisting(e.target.checked)} 
                    style={{ marginTop: '2px', width: '15px', height: '15px', accentColor: '#2563eb', cursor: 'pointer' }}
                  />
                  <span>
                    <strong>Also apply and reset password to "{defaultStudentPassword || '1234'}"</strong> for all existing students & parents now (even if parents previously changed it).
                  </span>
                </label>
              </div>

              <button 
                type="button"
                disabled={savingDefaultPassword}
                onClick={handleSaveDefaultPassword}
                style={{ padding: '9px 18px', background: savingDefaultPassword ? '#94a3b8' : '#2563eb', color: '#ffffff', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: '600', cursor: savingDefaultPassword ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', gap: '6px' }}
              >
                <Save size={14} className={savingDefaultPassword ? 'spin' : ''} /> {savingDefaultPassword ? 'Saving Policy & Passwords...' : 'Save Policy'}
              </button>
            </div>

            {/* Card 2: Bulk Reset All Students */}
            <div style={{ background: '#fffbeb', border: '1px solid #fef3c7', borderRadius: '12px', padding: '20px' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ fontSize: '18px' }}>🔄</span>
                  <h4 style={{ margin: 0, fontSize: '15px', color: '#92400e', fontWeight: '700' }}>Bulk Reset All Student Passwords</h4>
                </div>
                <span style={{ fontSize: '11px', fontWeight: '700', padding: '2px 8px', borderRadius: '12px', background: '#fef3c7', color: '#b45309', border: '1px solid #fde68a' }}>
                  All Existing Students
                </span>
              </div>
              <p style={{ fontSize: '13px', color: '#78350f', lineHeight: 1.5, marginBottom: '16px' }}>
                Quickly reset every student and parent account in your institution to a uniform password (e.g. <code>{defaultStudentPassword || 'Student@123'}</code>) so all parents can log in immediately.
              </p>

              <div style={{ marginBottom: '16px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                  <label style={{ fontSize: '12.5px', fontWeight: '600', color: '#92400e' }}>Target Reset Password</label>
                  {defaultStudentPassword && (
                    <span style={{ fontSize: '11.5px', color: '#b45309', fontWeight: '600' }}>
                      Current: {defaultStudentPassword}
                    </span>
                  )}
                </div>
                <input 
                  type="text" 
                  value={bulkResetPassword} 
                  onChange={e => setBulkResetPassword(e.target.value)} 
                  placeholder={`e.g. ${defaultStudentPassword || 'Student@123'}`}
                  style={{ width: '100%', padding: '10px 12px', border: '1px solid #fcd34d', borderRadius: '8px', fontSize: '13.5px', fontWeight: '600', color: '#78350f', background: '#fff' }} 
                />
              </div>

              <button 
                type="button"
                disabled={bulkResetting}
                onClick={handleBulkResetStudentPasswords}
                style={{ padding: '10px 18px', background: bulkResetting ? '#94a3b8' : '#d97706', color: '#ffffff', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: '600', cursor: bulkResetting ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', gap: '6px' }}
              >
                <RefreshCw size={14} className={bulkResetting ? 'spin' : ''} /> {bulkResetting ? 'Resetting All Accounts...' : 'Apply Bulk Password Reset to All Students'}
              </button>
            </div>

            {/* Card 3: Parent Photo Upload Permission */}
            <div style={{ background: profileForm.allowParentPhotoUpload !== false ? '#f0fdf4' : '#fff1f2', border: `1.5px solid ${profileForm.allowParentPhotoUpload !== false ? '#86efac' : '#fca5a5'}`, borderRadius: '12px', padding: '20px' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ fontSize: '20px' }}>📸</span>
                  <h4 style={{ margin: 0, fontSize: '15px', color: '#0f172a', fontWeight: '800' }}>Parent Child Photo Upload</h4>
                </div>
                <span style={{ 
                  padding: '3px 10px', 
                  borderRadius: '20px', 
                  fontSize: '11px', 
                  fontWeight: '800', 
                  background: profileForm.allowParentPhotoUpload !== false ? '#16a34a' : '#dc2626', 
                  color: '#ffffff' 
                }}>
                  {profileForm.allowParentPhotoUpload !== false ? '🟢 ENABLED' : '🔴 DISABLED'}
                </span>
              </div>
              <p style={{ fontSize: '13px', color: '#64748b', lineHeight: 1.5, marginBottom: '16px' }}>
                {profileForm.allowParentPhotoUpload !== false
                  ? 'Parents can take or choose a photo from their device gallery to update their child’s profile picture in the Parent App.'
                  : 'Parents CANNOT change child photos. Only School Administration and Class Teachers can assign official student ID photos.'}
              </p>

              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 14px', background: '#ffffff', border: '1px solid #cbd5e1', borderRadius: '8px', marginBottom: '16px' }}>
                <span style={{ fontSize: '13px', fontWeight: '700', color: profileForm.allowParentPhotoUpload !== false ? '#16a34a' : '#dc2626' }}>
                  {profileForm.allowParentPhotoUpload !== false ? 'Parents Allowed to Add/Change Photo' : 'Photo Upload Disabled for Parents'}
                </span>
                <input 
                  type="checkbox"
                  checked={profileForm.allowParentPhotoUpload !== false}
                  onChange={e => {
                    const newVal = e.target.checked;
                    setProfileForm(prev => ({ ...prev, allowParentPhotoUpload: newVal }));
                  }}
                  style={{ width: '20px', height: '20px', cursor: 'pointer' }}
                />
              </div>

              <button 
                type="button"
                disabled={profileSaving}
                onClick={handleSaveProfile}
                style={{ padding: '9px 18px', background: '#2563eb', color: '#ffffff', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: '700', cursor: profileSaving ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', gap: '6px' }}
              >
                <Save size={14} /> {profileSaving ? 'Saving Policy...' : 'Save Parent Photo Policy'}
              </button>
            </div>

            {/* Card 4: Teacher Student Photo Upload Permission */}
            <div style={{ background: profileForm.allowTeacherPhotoUpload !== false ? '#f0fdf4' : '#fff1f2', border: `1.5px solid ${profileForm.allowTeacherPhotoUpload !== false ? '#86efac' : '#fca5a5'}`, borderRadius: '12px', padding: '20px' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ fontSize: '20px' }}>👨‍🏫</span>
                  <h4 style={{ margin: 0, fontSize: '15px', color: '#0f172a', fontWeight: '800' }}>Teacher Student Photo Upload</h4>
                </div>
                <span style={{ 
                  padding: '3px 10px', 
                  borderRadius: '20px', 
                  fontSize: '11px', 
                  fontWeight: '800', 
                  background: profileForm.allowTeacherPhotoUpload !== false ? '#16a34a' : '#dc2626', 
                  color: '#ffffff' 
                }}>
                  {profileForm.allowTeacherPhotoUpload !== false ? '🟢 ENABLED' : '🔴 DISABLED'}
                </span>
              </div>
              <p style={{ fontSize: '13px', color: '#64748b', lineHeight: 1.5, marginBottom: '16px' }}>
                {profileForm.allowTeacherPhotoUpload !== false
                  ? 'Teachers can take photos using camera or select from device gallery to update student profile photos for their assigned classes in the Teacher Mobile App.'
                  : 'Teachers CANNOT upload or modify student profile photos. Only School Administration can upload official student photos.'}
              </p>

              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 14px', background: '#ffffff', border: '1px solid #cbd5e1', borderRadius: '8px', marginBottom: '16px' }}>
                <span style={{ fontSize: '13px', fontWeight: '700', color: profileForm.allowTeacherPhotoUpload !== false ? '#16a34a' : '#dc2626' }}>
                  {profileForm.allowTeacherPhotoUpload !== false ? 'Teachers Allowed to Add/Update Student Photos' : 'Student Photo Upload Disabled for Teachers'}
                </span>
                <input 
                  type="checkbox"
                  checked={profileForm.allowTeacherPhotoUpload !== false}
                  onChange={e => {
                    const newVal = e.target.checked;
                    setProfileForm(prev => ({ ...prev, allowTeacherPhotoUpload: newVal }));
                  }}
                  style={{ width: '20px', height: '20px', cursor: 'pointer' }}
                />
              </div>

              <button 
                type="button"
                disabled={profileSaving}
                onClick={handleSaveProfile}
                style={{ padding: '9px 18px', background: '#2563eb', color: '#ffffff', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: '700', cursor: profileSaving ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', gap: '6px' }}
              >
                <Save size={14} /> {profileSaving ? 'Saving Policy...' : 'Save Teacher Photo Policy'}
              </button>
            </div>
          </div>

          <div style={{ marginTop: '24px', padding: '14px 18px', background: '#eff6ff', borderRadius: '8px', border: '1px solid #bfdbfe', fontSize: '12.5px', color: '#1e40af', display: 'flex', alignItems: 'center', gap: '10px' }}>
            <span>💡</span>
            <span>
              <strong>Parent Mobile App Login:</strong> Parents log in by selecting the school, entering their child's <strong>Student ID</strong> (e.g. <code>SCH-0003</code>, <code>SCH-0004</code>), and this password.
            </span>
          </div>
        </div>
      )}

      {/* ── TAB 1: INSTITUTION PROFILE ── */}
      {activeSubTab === 'profile' && (
        <div style={{ background: '#ffffff', borderRadius: '12px', border: '1px solid #e2e8f0', padding: '24px' }}>
          <h3 style={{ fontSize: '16px', fontWeight: '700', color: '#0f172a', margin: '0 0 16px 0' }}>🏫 Institution Master Profile</h3>
          
          <form onSubmit={handleSaveProfile} style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '16px' }}>
            <div style={{ gridColumn: '1 / -1' }}>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: '700', color: '#1e293b', marginBottom: '4px' }}>
                🏛️ Trust / Society / Management Name (Optional)
              </label>
              <input 
                type="text" 
                placeholder="e.g. VIDYODAYA EDUCATION TRUST (R.)" 
                value={profileForm.trustName || ''} 
                onChange={e => {
                  const val = e.target.value;
                  setProfileForm(prev => ({ ...prev, trustName: val }));
                  setBranding(prev => ({ ...prev, trustName: val }));
                }} 
                style={{ width: '100%', padding: '9px 12px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '13px', fontWeight: '600', color: '#0f172a' }} 
              />
              <span style={{ fontSize: '11px', color: '#64748b', marginTop: '3px', display: 'block' }}>
                If entered, this trust name will be displayed at the very top of Fee Receipts, Marks Cards, ID Cards, Certificates & Official Documents. Leave blank if not applicable.
              </span>
            </div>

            <div>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#475569', marginBottom: '4px' }}>Institution Name *</label>
              <input type="text" value={profileForm.institutionName} onChange={e => setProfileForm({ ...profileForm, institutionName: e.target.value })} style={{ width: '100%', padding: '9px 12px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '13px' }} required />
            </div>

            <div>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#475569', marginBottom: '4px' }}>Institution Code / Org ID</label>
              <input type="text" value={profileForm.orgId} disabled style={{ width: '100%', padding: '9px 12px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '13px', background: '#f1f5f9', color: '#64748b' }} />
            </div>

            <div>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#475569', marginBottom: '4px' }}>Principal / Head Name</label>
              <input type="text" value={profileForm.principalName} onChange={e => setProfileForm({ ...profileForm, principalName: e.target.value })} style={{ width: '100%', padding: '9px 12px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '13px' }} />
            </div>

            <div>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#475569', marginBottom: '4px' }}>Board / Affiliation</label>
              <input type="text" value={profileForm.affiliation} onChange={e => setProfileForm({ ...profileForm, affiliation: e.target.value })} style={{ width: '100%', padding: '9px 12px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '13px' }} />
            </div>

            <div>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#475569', marginBottom: '4px' }}>Contact Phone</label>
              <input type="text" value={profileForm.phone} onChange={e => setProfileForm({ ...profileForm, phone: e.target.value })} style={{ width: '100%', padding: '9px 12px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '13px' }} />
            </div>

            <div>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#475569', marginBottom: '4px' }}>Official Email</label>
              <input type="email" value={profileForm.email} onChange={e => setProfileForm({ ...profileForm, email: e.target.value })} style={{ width: '100%', padding: '9px 12px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '13px' }} />
            </div>

            <div>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#475569', marginBottom: '4px' }}>Institution Type *</label>
              <select 
                value={profileForm.institutionType} 
                onChange={e => setProfileForm({ ...profileForm, institutionType: e.target.value })} 
                style={{ width: '100%', padding: '9px 12px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '13px', background: '#ffffff', fontWeight: '600', color: '#0f172a' }}
              >
                <option value="SCHOOL">🏫 School (Classes, Teachers, School Fees)</option>
                <option value="COLLEGE">🎓 College (Departments, Programs, Faculty, Semester Fees)</option>
              </select>
            </div>

            <div style={{ gridColumn: '1 / -1' }}>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#475569', marginBottom: '4px' }}>Campus Address</label>
              <textarea rows={2} value={profileForm.address} onChange={e => setProfileForm({ ...profileForm, address: e.target.value })} style={{ width: '100%', padding: '9px 12px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '13px' }} />
            </div>

            {/* ── Parent Portal Photo Upload Policy Switch Card ── */}
            <div style={{ 
              gridColumn: '1 / -1', 
              background: profileForm.allowParentPhotoUpload !== false ? '#f0fdf4' : '#fff1f2', 
              border: `1.5px solid ${profileForm.allowParentPhotoUpload !== false ? '#86efac' : '#fca5a5'}`, 
              borderRadius: '12px', 
              padding: '16px 20px', 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'space-between', 
              flexWrap: 'wrap', 
              gap: '16px' 
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '14px', flex: 1, minWidth: '280px' }}>
                <div style={{ 
                  width: '42px', 
                  height: '42px', 
                  borderRadius: '10px', 
                  background: profileForm.allowParentPhotoUpload !== false ? '#dcfce7' : '#fee2e2', 
                  color: profileForm.allowParentPhotoUpload !== false ? '#16a34a' : '#dc2626', 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center', 
                  fontSize: '22px' 
                }}>
                  📸
                </div>
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <h4 style={{ margin: 0, fontSize: '14.5px', fontWeight: '800', color: '#0f172a' }}>
                      Allow Parents to Add / Update Child Profile Photo
                    </h4>
                    <span style={{ 
                      padding: '2px 10px', 
                      borderRadius: '20px', 
                      fontSize: '11px', 
                      fontWeight: '800', 
                      background: profileForm.allowParentPhotoUpload !== false ? '#16a34a' : '#dc2626', 
                      color: '#ffffff' 
                    }}>
                      {profileForm.allowParentPhotoUpload !== false ? '🟢 ENABLED' : '🔴 DISABLED'}
                    </span>
                  </div>
                  <p style={{ margin: '4px 0 0 0', fontSize: '12.5px', color: '#64748b', lineHeight: 1.4 }}>
                    {profileForm.allowParentPhotoUpload !== false
                      ? 'Parents can capture or select a photo from gallery to update their child’s profile picture in the Parent Mobile App.'
                      : 'Parents CANNOT upload or modify child photos. Only School Administration and Class Teachers can set official student ID photos.'}
                  </p>
                </div>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontWeight: '700', fontSize: '13px', color: profileForm.allowParentPhotoUpload !== false ? '#16a34a' : '#dc2626' }}>
                  <input 
                    type="checkbox"
                    checked={profileForm.allowParentPhotoUpload !== false}
                    onChange={e => setProfileForm(prev => ({ ...prev, allowParentPhotoUpload: e.target.checked }))}
                    style={{ width: '20px', height: '20px', cursor: 'pointer' }}
                  />
                  <span>{profileForm.allowParentPhotoUpload !== false ? 'Allow Parent Upload' : 'Disable Parent Upload'}</span>
                </label>
              </div>
            </div>

            {/* ── Teacher Portal Student Photo Upload Policy Switch Card ── */}
            <div style={{ 
              gridColumn: '1 / -1', 
              background: profileForm.allowTeacherPhotoUpload !== false ? '#f0fdf4' : '#fff1f2', 
              border: `1.5px solid ${profileForm.allowTeacherPhotoUpload !== false ? '#86efac' : '#fca5a5'}`, 
              borderRadius: '12px', 
              padding: '16px 20px', 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'space-between', 
              flexWrap: 'wrap', 
              gap: '16px' 
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '14px', flex: 1, minWidth: '280px' }}>
                <div style={{ 
                  width: '42px', 
                  height: '42px', 
                  borderRadius: '10px', 
                  background: profileForm.allowTeacherPhotoUpload !== false ? '#dcfce7' : '#fee2e2', 
                  color: profileForm.allowTeacherPhotoUpload !== false ? '#16a34a' : '#dc2626', 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center', 
                  fontSize: '22px' 
                }}>
                  👨‍🏫
                </div>
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <h4 style={{ margin: 0, fontSize: '14.5px', fontWeight: '800', color: '#0f172a' }}>
                      Allow Teachers to Add / Update Student Profile Photo
                    </h4>
                    <span style={{ 
                      padding: '2px 10px', 
                      borderRadius: '20px', 
                      fontSize: '11px', 
                      fontWeight: '800', 
                      background: profileForm.allowTeacherPhotoUpload !== false ? '#16a34a' : '#dc2626', 
                      color: '#ffffff' 
                    }}>
                      {profileForm.allowTeacherPhotoUpload !== false ? '🟢 ENABLED' : '🔴 DISABLED'}
                    </span>
                  </div>
                  <p style={{ margin: '4px 0 0 0', fontSize: '12.5px', color: '#64748b', lineHeight: 1.4 }}>
                    {profileForm.allowTeacherPhotoUpload !== false
                      ? 'Teachers can take photos using camera or select from device gallery to update student profile photos for their assigned classes in the Teacher Mobile App.'
                      : 'Teachers CANNOT upload or modify student profile photos. Only School Administration can upload official student photos.'}
                  </p>
                </div>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontWeight: '700', fontSize: '13px', color: profileForm.allowTeacherPhotoUpload !== false ? '#16a34a' : '#dc2626' }}>
                  <input 
                    type="checkbox"
                    checked={profileForm.allowTeacherPhotoUpload !== false}
                    onChange={e => setProfileForm(prev => ({ ...prev, allowTeacherPhotoUpload: e.target.checked }))}
                    style={{ width: '20px', height: '20px', cursor: 'pointer' }}
                  />
                  <span>{profileForm.allowTeacherPhotoUpload !== false ? 'Allow Teacher Upload' : 'Disable Teacher Upload'}</span>
                </label>
              </div>
            </div>

            <div style={{ gridColumn: '1 / -1', display: 'flex', justifyContent: 'flex-end' }}>
              <button 
                type="submit" 
                disabled={profileSaving}
                style={{ 
                  padding: '10px 24px', 
                  background: profileSaving ? '#94a3b8' : '#2563eb', 
                  color: '#fff', 
                  border: 'none', 
                  borderRadius: '8px', 
                  fontSize: '13px', 
                  fontWeight: '600', 
                  cursor: profileSaving ? 'not-allowed' : 'pointer', 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '6px' 
                }}
              >
                {profileSaving ? 'Saving...' : 'Save Institution Profile'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* ── TAB 2: BRANDING & DOCUMENTS ── */}
      {activeSubTab === 'branding' && (
        <div style={{ background: '#ffffff', borderRadius: '12px', border: '1px solid #e2e8f0', padding: '24px' }}>
          <h3 style={{ fontSize: '16px', fontWeight: '700', color: '#0f172a', margin: '0 0 6px 0' }}>🎨 Institution Branding & Document Assets</h3>
          <p style={{ color: '#64748b', fontSize: '13px', marginBottom: '20px' }}>
            Uploaded logo, principal signature, official seal, and optional Trust/Society Name automatically apply across ID Cards, Fee Receipts, Report Cards, Certificates, and PDF Reports.
          </p>

          <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '10px', padding: '16px 20px', marginBottom: '24px' }}>
            <label style={{ display: 'block', fontSize: '12.5px', fontWeight: '700', color: '#1e293b', marginBottom: '6px' }}>
              🏛️ Educational Trust / Society / Management Name (Optional)
            </label>
            <input 
              type="text" 
              placeholder="e.g. VIDYODAYA EDUCATION TRUST (R.)" 
              value={branding.trustName || ''} 
              onChange={e => {
                const val = e.target.value;
                setBranding(prev => ({ ...prev, trustName: val }));
                setProfileForm(prev => ({ ...prev, trustName: val }));
              }} 
              style={{ width: '100%', maxWidth: '600px', padding: '9px 12px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '13px', fontWeight: '600', color: '#0f172a', background: '#ffffff' }} 
            />
            <p style={{ fontSize: '11.5px', color: '#64748b', margin: '6px 0 0 0', lineHeight: 1.4 }}>
              💡 If your school is operated by an educational trust or society, enter its name here. It will be printed directly above the school name across Fee Receipts, Marks Cards, ID Cards & Certificates. If left blank, it will not appear on any document.
            </p>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '20px', marginBottom: '24px' }}>
            
            {/* Logo Upload */}
            <div style={{ border: '1px dashed #cbd5e1', borderRadius: '10px', padding: '20px', textAlign: 'center', background: '#f8fafc' }}>
              <h4 style={{ margin: '0 0 12px 0', fontSize: '14px', color: '#334155' }}>School / College Logo</h4>
              {branding.logoUrl ? (
                <div>
                  <img 
                    src={resolveAssetUrl(branding.logoUrl)} 
                    alt="Logo" 
                    onError={(e) => {
                      e.target.style.display = 'none';
                      if (e.target.nextSibling) e.target.nextSibling.style.display = 'block';
                    }}
                    style={{ height: '80px', maxWidth: '100%', objectFit: 'contain', marginBottom: '12px', display: 'block', margin: '0 auto 12px auto' }} 
                  />
                  <div style={{ display: 'none', padding: '10px', color: '#94a3b8', fontSize: '12px' }}>Image not found. Please upload again.</div>
                  <div style={{ display: 'flex', justifyContent: 'center', gap: '8px' }}>
                    <label style={{ padding: '6px 12px', background: '#eff6ff', color: '#2563eb', border: '1px solid #bfdbfe', borderRadius: '6px', fontSize: '12px', fontWeight: '600', cursor: 'pointer' }}>
                      Replace <input type="file" accept="image/*" hidden onChange={e => handleUploadBrandingAsset('logo', e)} />
                    </label>
                    <button onClick={() => handleRemoveAsset('logo')} style={{ padding: '6px 12px', background: '#fef2f2', color: '#dc2626', border: '1px solid #fca5a5', borderRadius: '6px', fontSize: '12px', fontWeight: '600', cursor: 'pointer' }}>Remove</button>
                  </div>
                </div>
              ) : (
                <label style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'center', gap: '8px', cursor: 'pointer', padding: '20px' }}>
                  <Upload size={24} color="#2563eb" />
                  <span style={{ fontSize: '12.5px', fontWeight: '600', color: '#2563eb' }}>Upload School Logo</span>
                  <span style={{ fontSize: '11px', color: '#94a3b8' }}>PNG / JPG up to 5MB</span>
                  <input type="file" accept="image/*" hidden onChange={e => handleUploadBrandingAsset('logo', e)} />
                </label>
              )}
            </div>

            {/* Principal Signature Upload */}
            <div style={{ border: '1px dashed #cbd5e1', borderRadius: '10px', padding: '20px', textAlign: 'center', background: '#f8fafc' }}>
              <h4 style={{ margin: '0 0 12px 0', fontSize: '14px', color: '#334155' }}>Principal / Head Signature</h4>
              {branding.signatureUrl ? (
                <div>
                  <img 
                    src={resolveAssetUrl(branding.signatureUrl)} 
                    alt="Signature" 
                    onError={(e) => {
                      e.target.style.display = 'none';
                      if (e.target.nextSibling) e.target.nextSibling.style.display = 'block';
                    }}
                    style={{ height: '80px', maxWidth: '100%', objectFit: 'contain', marginBottom: '12px', display: 'block', margin: '0 auto 12px auto' }} 
                  />
                  <div style={{ display: 'none', padding: '10px', color: '#94a3b8', fontSize: '12px' }}>Image not found. Please upload again.</div>
                  <div style={{ display: 'flex', justifyContent: 'center', gap: '8px' }}>
                    <label style={{ padding: '6px 12px', background: '#eff6ff', color: '#2563eb', border: '1px solid #bfdbfe', borderRadius: '6px', fontSize: '12px', fontWeight: '600', cursor: 'pointer' }}>
                      Replace <input type="file" accept="image/*" hidden onChange={e => handleUploadBrandingAsset('signature', e)} />
                    </label>
                    <button onClick={() => handleRemoveAsset('signature')} style={{ padding: '6px 12px', background: '#fef2f2', color: '#dc2626', border: '1px solid #fca5a5', borderRadius: '6px', fontSize: '12px', fontWeight: '600', cursor: 'pointer' }}>Remove</button>
                  </div>
                </div>
              ) : (
                <label style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'center', gap: '8px', cursor: 'pointer', padding: '20px' }}>
                  <Upload size={24} color="#16a34a" />
                  <span style={{ fontSize: '12.5px', fontWeight: '600', color: '#16a34a' }}>Upload Signature</span>
                  <span style={{ fontSize: '11px', color: '#94a3b8' }}>Transparent PNG recommended</span>
                  <input type="file" accept="image/*" hidden onChange={e => handleUploadBrandingAsset('signature', e)} />
                </label>
              )}
            </div>

            {/* Official Seal Upload */}
            <div style={{ border: '1px dashed #cbd5e1', borderRadius: '10px', padding: '20px', textAlign: 'center', background: '#f8fafc' }}>
              <h4 style={{ margin: '0 0 12px 0', fontSize: '14px', color: '#334155' }}>Official Stamp / Seal</h4>
              {branding.sealUrl ? (
                <div>
                  <img 
                    src={resolveAssetUrl(branding.sealUrl)} 
                    alt="Seal" 
                    onError={(e) => {
                      e.target.style.display = 'none';
                      if (e.target.nextSibling) e.target.nextSibling.style.display = 'block';
                    }}
                    style={{ height: '80px', maxWidth: '100%', objectFit: 'contain', marginBottom: '12px', display: 'block', margin: '0 auto 12px auto' }} 
                  />
                  <div style={{ display: 'none', padding: '10px', color: '#94a3b8', fontSize: '12px' }}>Image not found. Please upload again.</div>
                  <div style={{ display: 'flex', justifyContent: 'center', gap: '8px' }}>
                    <label style={{ padding: '6px 12px', background: '#eff6ff', color: '#2563eb', border: '1px solid #bfdbfe', borderRadius: '6px', fontSize: '12px', fontWeight: '600', cursor: 'pointer' }}>
                      Replace <input type="file" accept="image/*" hidden onChange={e => handleUploadBrandingAsset('seal', e)} />
                    </label>
                    <button onClick={() => handleRemoveAsset('seal')} style={{ padding: '6px 12px', background: '#fef2f2', color: '#dc2626', border: '1px solid #fca5a5', borderRadius: '6px', fontSize: '12px', fontWeight: '600', cursor: 'pointer' }}>Remove</button>
                  </div>
                </div>
              ) : (
                <label style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'center', gap: '8px', cursor: 'pointer', padding: '20px' }}>
                  <Upload size={24} color="#9333ea" />
                  <span style={{ fontSize: '12.5px', fontWeight: '600', color: '#9333ea' }}>Upload Institution Seal</span>
                  <span style={{ fontSize: '11px', color: '#94a3b8' }}>Transparent PNG recommended</span>
                  <input type="file" accept="image/*" hidden onChange={e => handleUploadBrandingAsset('seal', e)} />
                </label>
              )}
            </div>

          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <button onClick={handleSaveBranding} disabled={brandingLoading} style={{ padding: '10px 24px', background: '#2563eb', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: '600', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Save size={15} /> {brandingLoading ? 'Saving...' : 'Save Branding Defaults'}
            </button>
          </div>
        </div>
      )}

      {/* ── TAB 3: DYNAMIC ID & SEQUENCE SETTINGS ── */}
      {activeSubTab === 'student_id' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
          
          {/* STUDENT IDs CARD */}
          <div style={{ background: '#ffffff', borderRadius: '12px', border: '1px solid #e2e8f0', padding: '24px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', gap: '10px' }}>
              <div>
                <h3 style={{ fontSize: '16px', fontWeight: '700', color: '#0f172a', margin: 0 }}>🆔 Student Identifier Configuration</h3>
                <p style={{ color: '#64748b', fontSize: '13px', margin: '4px 0 0 0' }}>Configure automatic Student ID generation format and sequence reset rules.</p>
              </div>
              <button onClick={() => triggerResetSequence('STUDENT')} style={{ padding: '8px 16px', background: '#fee2e2', color: '#991b1b', border: '1px solid #fca5a5', borderRadius: '8px', fontSize: '12.5px', fontWeight: '600', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px' }}>
                <RefreshCw size={14} /> Reset Sequence
              </button>
            </div>

            {/* Live Preview Box */}
            <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '10px', padding: '16px 20px', marginBottom: '24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
              <div>
                <span style={{ fontSize: '12px', fontWeight: '700', color: '#1e40af', display: 'block', marginBottom: '4px' }}>LIVE FORMAT PREVIEW</span>
                <strong style={{ fontSize: '20px', color: '#2563eb', fontFamily: 'monospace' }}>{studentIdLivePreview}</strong>
              </div>
              <div>
                <span style={{ fontSize: '12px', fontWeight: '700', color: '#155724', display: 'block', marginBottom: '4px' }}>NEXT SEQUENCE ID</span>
                <strong style={{ fontSize: '20px', color: '#16a34a', fontFamily: 'monospace' }}>{studentIdNextPreview}</strong>
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '16px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#475569', marginBottom: '4px' }}>Generation Mode</label>
                <select value={studentIdConfig.mode} onChange={e => setStudentIdConfig({ ...studentIdConfig, mode: e.target.value })} style={{ width: '100%', padding: '9px 12px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '13px' }}>
                  <option value="AUTOMATIC">Automatic (Recommended)</option>
                  <option value="MANUAL">Manual Entry</option>
                </select>
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#475569', marginBottom: '4px' }}>Prefix (e.g. SCH, STU)</label>
                <input type="text" value={studentIdConfig.prefix} onChange={e => setStudentIdConfig({ ...studentIdConfig, prefix: e.target.value.toUpperCase() })} style={{ width: '100%', padding: '9px 12px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '13px' }} />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#475569', marginBottom: '4px' }}>Include Academic Year</label>
                <select value={studentIdConfig.includeYear ? 'YES' : 'NO'} onChange={e => setStudentIdConfig({ ...studentIdConfig, includeYear: e.target.value === 'YES' })} style={{ width: '100%', padding: '9px 12px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '13px' }}>
                  <option value="YES">Yes (Include 2026)</option>
                  <option value="NO">No (Omit Year)</option>
                </select>
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#475569', marginBottom: '4px' }}>Separator</label>
                <select value={studentIdConfig.separator} onChange={e => setStudentIdConfig({ ...studentIdConfig, separator: e.target.value })} style={{ width: '100%', padding: '9px 12px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '13px' }}>
                  <option value="-">- (Hyphen)</option>
                  <option value="/">/ (Slash)</option>
                  <option value="">None</option>
                </select>
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#475569', marginBottom: '4px' }}>Sequence Length (Digits)</label>
                <select value={studentIdConfig.padding} onChange={e => setStudentIdConfig({ ...studentIdConfig, padding: Number(e.target.value) })} style={{ width: '100%', padding: '9px 12px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '13px' }}>
                  <option value={3}>3 Digits (001)</option>
                  <option value={4}>4 Digits (0001)</option>
                  <option value={5}>5 Digits (00001)</option>
                  <option value={6}>6 Digits (000001)</option>
                </select>
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#475569', marginBottom: '4px' }}>Reset Policy</label>
                <select value={studentIdConfig.resetPolicy} onChange={e => setStudentIdConfig({ ...studentIdConfig, resetPolicy: e.target.value })} style={{ width: '100%', padding: '9px 12px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '13px' }}>
                  <option value="ACADEMIC_YEAR">Every Academic Year</option>
                  <option value="NEVER">Never (Continuous Global Sequence)</option>
                </select>
              </div>
            </div>
          </div>
 
          {/* TEACHER IDs CARD */}
          <div style={{ background: '#ffffff', borderRadius: '12px', border: '1px solid #e2e8f0', padding: '24px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', gap: '10px' }}>
              <div>
                <h3 style={{ fontSize: '16px', fontWeight: '700', color: '#0f172a', margin: 0 }}>👨‍🏫 Teacher Identifier Configuration</h3>
                <p style={{ color: '#64748b', fontSize: '13px', margin: '4px 0 0 0' }}>Configure automatic Teacher ID / Employee Code format and sequence resets.</p>
              </div>
              <button onClick={() => triggerResetSequence('TEACHER')} style={{ padding: '8px 16px', background: '#fee2e2', color: '#991b1b', border: '1px solid #fca5a5', borderRadius: '8px', fontSize: '12.5px', fontWeight: '600', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px' }}>
                <RefreshCw size={14} /> Reset Sequence
              </button>
            </div>
 
            {/* Live Preview Box */}
            <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '10px', padding: '16px 20px', marginBottom: '24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
              <div>
                <span style={{ fontSize: '12px', fontWeight: '700', color: '#166534', display: 'block', marginBottom: '4px' }}>LIVE FORMAT PREVIEW</span>
                <strong style={{ fontSize: '20px', color: '#16a34a', fontFamily: 'monospace' }}>{teacherIdLivePreview}</strong>
              </div>
              <div>
                <span style={{ fontSize: '12px', fontWeight: '700', color: '#1e40af', display: 'block', marginBottom: '4px' }}>NEXT SEQUENCE ID</span>
                <strong style={{ fontSize: '20px', color: '#2563eb', fontFamily: 'monospace' }}>{teacherIdNextPreview}</strong>
              </div>
            </div>
 
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '16px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#475569', marginBottom: '4px' }}>Generation Mode</label>
                <select value={teacherIdConfig.mode} onChange={e => setTeacherIdConfig({ ...teacherIdConfig, mode: e.target.value })} style={{ width: '100%', padding: '9px 12px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '13px' }}>
                  <option value="AUTOMATIC">Automatic Sequential (Recommended)</option>
                  <option value="MANUAL">Manual Entry</option>
                </select>
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#475569', marginBottom: '4px' }}>Prefix (e.g. TCH, FAC, EMP)</label>
                <input type="text" value={teacherIdConfig.prefix} onChange={e => setTeacherIdConfig({ ...teacherIdConfig, prefix: e.target.value.toUpperCase() })} style={{ width: '100%', padding: '9px 12px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '13px' }} />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#475569', marginBottom: '4px' }}>Include Year</label>
                <select value={teacherIdConfig.includeYear ? 'YES' : 'NO'} onChange={e => setTeacherIdConfig({ ...teacherIdConfig, includeYear: e.target.value === 'YES' })} style={{ width: '100%', padding: '9px 12px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '13px' }}>
                  <option value="NO">No (Omit Year)</option>
                  <option value="YES">Yes (Include 2026)</option>
                </select>
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#475569', marginBottom: '4px' }}>Separator</label>
                <select value={teacherIdConfig.separator} onChange={e => setTeacherIdConfig({ ...teacherIdConfig, separator: e.target.value })} style={{ width: '100%', padding: '9px 12px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '13px' }}>
                  <option value="-">- (Hyphen)</option>
                  <option value="/">/ (Slash)</option>
                  <option value="">None</option>
                </select>
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#475569', marginBottom: '4px' }}>Sequence Digits</label>
                <select value={teacherIdConfig.padding} onChange={e => setTeacherIdConfig({ ...teacherIdConfig, padding: Number(e.target.value) })} style={{ width: '100%', padding: '9px 12px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '13px' }}>
                  <option value={3}>3 Digits (101 / 001)</option>
                  <option value={4}>4 Digits (0001)</option>
                  <option value={5}>5 Digits (00001)</option>
                </select>
              </div>
            </div>
          </div>
 
          {/* LIBRARY MEMBER IDs CARD */}
          <div style={{ background: '#ffffff', borderRadius: '12px', border: '1px solid #e2e8f0', padding: '24px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', gap: '10px' }}>
              <div>
                <h3 style={{ fontSize: '16px', fontWeight: '700', color: '#0f172a', margin: 0 }}>📚 Library Member ID Configuration</h3>
                <p style={{ color: '#64748b', fontSize: '13px', margin: '4px 0 0 0' }}>Configure automatic Library Card identifier format and sequence resets.</p>
              </div>
              <button onClick={() => triggerResetSequence('LIBRARY_MEMBER')} style={{ padding: '8px 16px', background: '#fee2e2', color: '#991b1b', border: '1px solid #fca5a5', borderRadius: '8px', fontSize: '12.5px', fontWeight: '600', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px' }}>
                <RefreshCw size={14} /> Reset Sequence
              </button>
            </div>
 
            {/* Live Preview Box */}
            <div style={{ background: '#faf5ff', border: '1px solid #e9d5ff', borderRadius: '10px', padding: '16px 20px', marginBottom: '24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
              <div>
                <span style={{ fontSize: '12px', fontWeight: '700', color: '#6b21a8', display: 'block', marginBottom: '4px' }}>LIVE FORMAT PREVIEW</span>
                <strong style={{ fontSize: '20px', color: '#7c3aed', fontFamily: 'monospace' }}>{libraryIdLivePreview}</strong>
              </div>
              <div>
                <span style={{ fontSize: '12px', fontWeight: '700', color: '#1e40af', display: 'block', marginBottom: '4px' }}>NEXT SEQUENCE ID</span>
                <strong style={{ fontSize: '20px', color: '#2563eb', fontFamily: 'monospace' }}>{libraryIdNextPreview}</strong>
              </div>
            </div>
 
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '16px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#475569', marginBottom: '4px' }}>Generation Mode</label>
                <select value={libraryIdConfig.mode} onChange={e => setLibraryIdConfig({ ...libraryIdConfig, mode: e.target.value })} style={{ width: '100%', padding: '9px 12px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '13px' }}>
                  <option value="AUTOMATIC">Automatic Sequential (Recommended)</option>
                  <option value="MANUAL">Manual Entry</option>
                </select>
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#475569', marginBottom: '4px' }}>Prefix (e.g. LIB, CARD)</label>
                <input type="text" value={libraryIdConfig.prefix} onChange={e => setLibraryIdConfig({ ...libraryIdConfig, prefix: e.target.value.toUpperCase() })} style={{ width: '100%', padding: '9px 12px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '13px' }} />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#475569', marginBottom: '4px' }}>Include Year</label>
                <select value={libraryIdConfig.includeYear ? 'YES' : 'NO'} onChange={e => setLibraryIdConfig({ ...libraryIdConfig, includeYear: e.target.value === 'YES' })} style={{ width: '100%', padding: '9px 12px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '13px' }}>
                  <option value="NO">No (Omit Year)</option>
                  <option value="YES">Yes (Include 2026)</option>
                </select>
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#475569', marginBottom: '4px' }}>Separator</label>
                <select value={libraryIdConfig.separator} onChange={e => setLibraryIdConfig({ ...libraryIdConfig, separator: e.target.value })} style={{ width: '100%', padding: '9px 12px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '13px' }}>
                  <option value="-">- (Hyphen)</option>
                  <option value="/">/ (Slash)</option>
                  <option value="">None</option>
                </select>
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#475569', marginBottom: '4px' }}>Sequence Digits</label>
                <select value={libraryIdConfig.padding} onChange={e => setLibraryIdConfig({ ...libraryIdConfig, padding: Number(e.target.value) })} style={{ width: '100%', padding: '9px 12px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '13px' }}>
                  <option value={3}>3 Digits (001)</option>
                  <option value={4}>4 Digits (0001)</option>
                  <option value={5}>5 Digits (00001)</option>
                </select>
              </div>
            </div>
          </div>
 
          {/* FEE RECEIPT IDs CARD */}
          <div style={{ background: '#ffffff', borderRadius: '12px', border: '1px solid #e2e8f0', padding: '24px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', gap: '10px' }}>
              <div>
                <h3 style={{ fontSize: '16px', fontWeight: '700', color: '#0f172a', margin: 0 }}>🧾 Fee Receipt ID Configuration</h3>
                <p style={{ color: '#64748b', fontSize: '13px', margin: '4px 0 0 0' }}>Configure automatic Fee Receipt prefix, academic year pattern and resets.</p>
              </div>
              <button onClick={() => triggerResetSequence('FEE_RECEIPT')} style={{ padding: '8px 16px', background: '#fee2e2', color: '#991b1b', border: '1px solid #fca5a5', borderRadius: '8px', fontSize: '12.5px', fontWeight: '600', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px' }}>
                <RefreshCw size={14} /> Reset Sequence
              </button>
            </div>
 
            {/* Live Preview Box */}
            <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: '10px', padding: '16px 20px', marginBottom: '24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
              <div>
                <span style={{ fontSize: '12px', fontWeight: '700', color: '#b45309', display: 'block', marginBottom: '4px' }}>LIVE FORMAT PREVIEW</span>
                <strong style={{ fontSize: '20px', color: '#d97706', fontFamily: 'monospace' }}>{receiptIdLivePreview}</strong>
              </div>
              <div>
                <span style={{ fontSize: '12px', fontWeight: '700', color: '#166534', display: 'block', marginBottom: '4px' }}>NEXT SEQUENCE ID</span>
                <strong style={{ fontSize: '20px', color: '#16a34a', fontFamily: 'monospace' }}>{receiptIdNextPreview}</strong>
              </div>
            </div>
 
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '16px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#475569', marginBottom: '4px' }}>Generation Mode</label>
                <select value={receiptIdConfig.mode} onChange={e => setReceiptIdConfig({ ...receiptIdConfig, mode: e.target.value })} style={{ width: '100%', padding: '9px 12px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '13px' }}>
                  <option value="AUTOMATIC">Automatic Sequential (Recommended)</option>
                  <option value="MANUAL">Manual Entry</option>
                </select>
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#475569', marginBottom: '4px' }}>Prefix (e.g. RCPT, REC)</label>
                <input type="text" value={receiptIdConfig.prefix} onChange={e => setReceiptIdConfig({ ...receiptIdConfig, prefix: e.target.value.toUpperCase() })} style={{ width: '100%', padding: '9px 12px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '13px' }} />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#475569', marginBottom: '4px' }}>Include Academic Year</label>
                <select value={receiptIdConfig.includeYear ? 'YES' : 'NO'} onChange={e => setReceiptIdConfig({ ...receiptIdConfig, includeYear: e.target.value === 'YES' })} style={{ width: '100%', padding: '9px 12px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '13px' }}>
                  <option value="YES">Yes (Include 2026)</option>
                  <option value="NO">No (Omit Year)</option>
                </select>
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#475569', marginBottom: '4px' }}>Separator</label>
                <select value={receiptIdConfig.separator} onChange={e => setReceiptIdConfig({ ...receiptIdConfig, separator: e.target.value })} style={{ width: '100%', padding: '9px 12px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '13px' }}>
                  <option value="-">- (Hyphen)</option>
                  <option value="/">/ (Slash)</option>
                  <option value="">None</option>
                </select>
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#475569', marginBottom: '4px' }}>Sequence Digits</label>
                <select value={receiptIdConfig.padding} onChange={e => setReceiptIdConfig({ ...receiptIdConfig, padding: Number(e.target.value) })} style={{ width: '100%', padding: '9px 12px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '13px' }}>
                  <option value={4}>4 Digits (0001)</option>
                  <option value={5}>5 Digits (00001)</option>
                  <option value={6}>6 Digits (000001)</option>
                </select>
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#475569', marginBottom: '4px' }}>Reset Policy</label>
                <select value={receiptIdConfig.resetPolicy} onChange={e => setReceiptIdConfig({ ...receiptIdConfig, resetPolicy: e.target.value })} style={{ width: '100%', padding: '9px 12px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '13px' }}>
                  <option value="ACADEMIC_YEAR">Every Academic Year</option>
                  <option value="NEVER">Never (Continuous Global Sequence)</option>
                </select>
              </div>
            </div>
          </div>
 
          {/* GLOBAL SAVE ACTION BUTTON */}
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '8px' }}>
            <button onClick={handleSaveIdSettings} disabled={studentIdSaving} style={{ padding: '12px 28px', background: '#2563eb', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '13.5px', fontWeight: '600', cursor: studentIdSaving ? 'not-allowed' : 'pointer', opacity: studentIdSaving ? 0.6 : 1, display: 'flex', alignItems: 'center', gap: '8px', boxShadow: '0 4px 6px -1px rgba(37, 99, 235, 0.2)' }}>
              <Save size={16} /> {studentIdSaving ? 'Saving Configurations...' : 'Save All ID Generator Settings'}
            </button>
          </div>
        </div>
      )}

      {/* ── TAB 4: ROLL NUMBER SETTINGS ── */}
      {activeSubTab === 'roll_no' && (
        <div style={{ background: '#ffffff', borderRadius: '12px', border: '1px solid #e2e8f0', padding: '24px' }}>
          <h3 style={{ fontSize: '16px', fontWeight: '700', color: '#0f172a', margin: '0 0 6px 0' }}>🔢 Class & Section Roll Number Configuration</h3>
          <p style={{ color: '#64748b', fontSize: '13px', marginBottom: '20px' }}>
            Roll numbers are class & section-specific sequence numbers (e.g., LKG-A Roll No 01, 02...).
          </p>

          {/* Live Preview Box */}
          <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '10px', padding: '16px 20px', marginBottom: '24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
            <div>
              <span style={{ fontSize: '12px', fontWeight: '700', color: '#475569', display: 'block', marginBottom: '4px' }}>LIVE FORMAT PREVIEW</span>
              <strong style={{ fontSize: '20px', color: '#0f172a', fontFamily: 'monospace' }}>{rollNoConfig.preview || 'Save to preview'}</strong>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '16px', marginBottom: '20px' }}>
            <div>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#475569', marginBottom: '4px' }}>Roll Number Mode</label>
              <select value={rollNoConfig.rollNumberMode} onChange={e => setRollNoConfig({ ...rollNoConfig, rollNumberMode: e.target.value })} style={{ width: '100%', padding: '9px 12px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '13px' }}>
                <option value="SEQUENTIAL_PER_CLASS">Sequential per Class</option>
                <option value="SEQUENTIAL_PER_SECTION">Sequential per Section</option>
                <option value="ALPHABETICAL">Alphabetical</option>
                <option value="ADMISSION_ORDER">Admission Order</option>
                <option value="MANUAL">Manual</option>
                <option value="CUSTOM">Custom</option>
              </select>
            </div>

            <div>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#475569', marginBottom: '4px' }}>Assignment Ordering</label>
              <select value={rollNoConfig.assignmentOrdering} onChange={e => setRollNoConfig({ ...rollNoConfig, assignmentOrdering: e.target.value })} style={{ width: '100%', padding: '9px 12px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '13px' }}>
                <option value="ALPHABETICAL">Alphabetical by Student Name</option>
                <option value="ADMISSION_DATE">Admission Date / Enrollment Order</option>
                <option value="STUDENT_ID">Student ID</option>
                <option value="REGISTRATION_DATE">Registration Date</option>
                <option value="MANUAL">Manual</option>
              </select>
            </div>

            <div>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#475569', marginBottom: '4px' }}>Starting Roll Number</label>
              <input type="number" min="1" value={rollNoConfig.startingRollNumber} onChange={e => setRollNoConfig({ ...rollNoConfig, startingRollNumber: Number(e.target.value) })} style={{ width: '100%', padding: '9px 12px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '13px' }} />
            </div>

            <div>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#475569', marginBottom: '4px' }}>Padding (Digits)</label>
              <input type="number" min="1" value={rollNoConfig.padding} onChange={e => setRollNoConfig({ ...rollNoConfig, padding: Number(e.target.value) })} style={{ width: '100%', padding: '9px 12px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '13px' }} />
            </div>

            <div>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#475569', marginBottom: '4px' }}>Prefix (e.g. ROLL)</label>
              <input type="text" value={rollNoConfig.prefix} onChange={e => setRollNoConfig({ ...rollNoConfig, prefix: e.target.value.toUpperCase() })} style={{ width: '100%', padding: '9px 12px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '13px' }} />
            </div>
            
            <div>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#475569', marginBottom: '4px' }}>Separator</label>
              <select value={rollNoConfig.separator} onChange={e => setRollNoConfig({ ...rollNoConfig, separator: e.target.value })} style={{ width: '100%', padding: '9px 12px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '13px' }}>
                <option value="-">- (Hyphen)</option>
                <option value="/">/ (Slash)</option>
                <option value="">None</option>
              </select>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', gridColumn: '1 / -1' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: '#334155', cursor: 'pointer' }}>
                <input type="checkbox" checked={rollNoConfig.resetPolicy === 'ACADEMIC_YEAR'} onChange={e => setRollNoConfig({ ...rollNoConfig, resetPolicy: e.target.checked ? 'ACADEMIC_YEAR' : 'NEVER' })} />
                Reset Every Academic Year
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: '#334155', cursor: 'pointer' }}>
                <input type="checkbox" checked={rollNoConfig.regenerateOnPromotion} onChange={e => setRollNoConfig({ ...rollNoConfig, regenerateOnPromotion: e.target.checked })} />
                Regenerate On Promotion
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: '#334155', cursor: 'pointer' }}>
                <input type="checkbox" checked={rollNoConfig.allowManualOverride} onChange={e => setRollNoConfig({ ...rollNoConfig, allowManualOverride: e.target.checked })} />
                Allow Manual Override
              </label>
            </div>
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
            <button onClick={handleRegenerateRollNumbers} style={{ padding: '10px 24px', background: '#fee2e2', color: '#991b1b', border: '1px solid #fca5a5', borderRadius: '8px', fontSize: '13px', fontWeight: '600', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px' }}>
              <RefreshCw size={15} /> Regenerate Roll Numbers
            </button>
            <button onClick={handleSaveRollNoRules} style={{ padding: '10px 24px', background: '#2563eb', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: '600', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Save size={15} /> Save Roll Number Rules
            </button>
          </div>
        </div>
      )}

      {/* ── TAB 5: ACADEMIC SETTINGS ── */}
      {activeSubTab === 'academic' && (
        <div style={{ background: '#ffffff', borderRadius: '12px', border: '1px solid #e2e8f0', padding: '24px' }}>
          <h3 style={{ fontSize: '16px', fontWeight: '700', color: '#0f172a', margin: '0 0 16px 0' }}>📅 Academic Session & Promotion Rules</h3>

          {/* Declare New Academic Year Box */}
          <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '10px', padding: '16px', marginBottom: '20px' }}>
            <h4 style={{ margin: '0 0 10px 0', fontSize: '14px', color: '#1e293b' }}>✨ Declare / Create New Academic Year</h4>
            <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'center' }}>
              <input 
                type="text" 
                placeholder="e.g. 2027-28" 
                value={newAcademicYearInput} 
                onChange={e => setNewAcademicYearInput(e.target.value)} 
                style={{ padding: '8px 12px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '13px', width: '180px' }} 
              />
              <button 
                type="button"
                onClick={handleDeclareAcademicYear} 
                style={{ padding: '8px 16px', background: '#059669', color: '#fff', border: 'none', borderRadius: '6px', fontSize: '12.5px', fontWeight: '600', cursor: 'pointer' }}
              >
                + Declare Academic Year
              </button>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '16px', marginBottom: '20px' }}>
            <div>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#475569', marginBottom: '4px' }}>Current Active Academic Session</label>
              <select value={academicConfig.currentAcademicYear} onChange={e => setAcademicConfig({ ...academicConfig, currentAcademicYear: e.target.value })} style={{ width: '100%', padding: '9px 12px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '13px' }}>
                {academicYearsList.map(yr => (
                  <option key={yr} value={yr}>AY {yr} {yr === academicConfig.currentAcademicYear ? '(Current Active)' : ''}</option>
                ))}
              </select>
            </div>

            <div>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#475569', marginBottom: '4px' }}>Institution Type</label>
              <input type="text" value="SCHOOL" disabled style={{ width: '100%', padding: '9px 12px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '13px', background: '#f1f5f9' }} />
            </div>

            <div>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#475569', marginBottom: '4px' }}>Minimum Attendance for Promotion (%)</label>
              <input type="number" value={academicConfig.minimumAttendance} onChange={e => setAcademicConfig({ ...academicConfig, minimumAttendance: Number(e.target.value) })} style={{ width: '100%', padding: '9px 12px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '13px' }} />
            </div>
          </div>

          {/* Configurable Class Progression Mapping */}
          <div style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '10px', padding: '20px', marginTop: '20px', marginBottom: '20px' }}>
            <h4 style={{ margin: '0 0 6px 0', fontSize: '15px', color: '#0f172a', fontWeight: '700' }}>🔄 Configurable Class Progression Mapping</h4>
            <p style={{ color: '#64748b', fontSize: '12.5px', marginBottom: '16px' }}>
              Define default progression rules for end-of-year promotions across all school levels.
            </p>

            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
              <thead>
                <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0', textAlign: 'left' }}>
                  <th style={{ padding: '10px' }}>Current Class</th>
                  <th style={{ padding: '10px' }}>Promote To Target Class</th>
                </tr>
              </thead>
              <tbody>
                {classProgressionRules.map((rule, idx) => (
                  <tr key={idx} style={{ borderBottom: '1px solid #f1f5f9' }}>
                    <td style={{ padding: '8px 10px', fontWeight: '700', color: '#1e293b' }}>{rule.from}</td>
                    <td style={{ padding: '8px 10px' }}>
                      <input 
                        type="text" 
                        value={rule.to} 
                        onChange={e => {
                          const val = e.target.value;
                          setClassProgressionRules(prev => prev.map((r, i) => i === idx ? { ...r, to: val } : r));
                        }} 
                        style={{ padding: '6px 10px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '12.5px', width: '200px' }} 
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <button onClick={handleSaveAcademicSettings} disabled={academicSaving} style={{ padding: '10px 24px', background: '#2563eb', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: '600', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Save size={15} /> {academicSaving ? 'Saving...' : 'Save Academic Rules & Progression'}
            </button>
          </div>
        </div>
      )}

      {activeSubTab === 'report_card_templates' && (
        <ReportCardTemplateDesigner 
          orgId={orgData?.orgId || 'SCH-02'} 
          branding={branding} 
          orgName={orgData?.orgName || orgData?.name || ''}
          orgAddress={orgData?.address || ''}
        />
      )}

      {/* ── TAB 6: STAFF USERS ── */}
      {activeSubTab === 'users' && (
        <StaffUserManagement showNotification={showNotification} />
      )}

      {/* ── TAB 7: PERMISSIONS ── */}
      {activeSubTab === 'permissions' && (
        <PermissionsMatrix showNotification={showNotification} />
      )}

      {/* ── TAB 8: PAYMENT & BANK ── */}
      {activeSubTab === 'payment' && (
        <PaymentBankSettings orgData={orgData} showNotification={showNotification} />
      )}

      {/* ── TAB 5b: LIBRARY RULES ── */}
      {activeSubTab === 'library' && (
        <div style={{ background: '#ffffff', borderRadius: '12px', border: '1px solid #e2e8f0', padding: '24px' }}>
          <h3 style={{ fontSize: '16px', fontWeight: '700', color: '#0f172a', margin: '0 0 16px 0' }}>📚 Institution Library Rules & Borrowing Policy</h3>

          <form onSubmit={e => { e.preventDefault(); if (showNotification) showNotification('success', 'Library Settings Saved', 'Borrowing limits and overdue fine policies updated'); }} style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '16px' }}>
            <div>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#475569', marginBottom: '4px' }}>Student Borrowing Limit (Books)</label>
              <input type="number" defaultValue="3" style={{ width: '100%', padding: '9px 12px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '13px' }} />
            </div>

            <div>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#475569', marginBottom: '4px' }}>Teacher Borrowing Limit (Books)</label>
              <input type="number" defaultValue="5" style={{ width: '100%', padding: '9px 12px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '13px' }} />
            </div>

            <div>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#475569', marginBottom: '4px' }}>Student Loan Duration (Days)</label>
              <input type="number" defaultValue="14" style={{ width: '100%', padding: '9px 12px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '13px' }} />
            </div>

            <div>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#475569', marginBottom: '4px' }}>Teacher Loan Duration (Days)</label>
              <input type="number" defaultValue="30" style={{ width: '100%', padding: '9px 12px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '13px' }} />
            </div>

            <div>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#475569', marginBottom: '4px' }}>Fine Rate Per Overdue Day (₹)</label>
              <input type="number" defaultValue="5" style={{ width: '100%', padding: '9px 12px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '13px' }} />
            </div>

            <div>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#475569', marginBottom: '4px' }}>Grace Period (Free Days)</label>
              <input type="number" defaultValue="1" style={{ width: '100%', padding: '9px 12px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '13px' }} />
            </div>

            <div style={{ gridColumn: '1 / -1', display: 'flex', justifyContent: 'flex-end', marginTop: '12px' }}>
              <button type="submit" style={{ padding: '10px 24px', background: '#2563eb', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: '600', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px' }}>
                <Save size={15} /> Save Library Policy Rules
              </button>
            </div>
          </form>
        </div>
      )}

      {/* ── TAB: MEDIA & STORAGE MANAGEMENT ── */}
      {activeSubTab === 'storage' && (
        <StorageManagement orgData={orgData} showNotification={showNotification} />
      )}

      {/* ── RESET SEQUENCE DIALOG MODAL ── */}
      {resetModalOpen && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(15, 23, 42, 0.6)',
          backdropFilter: 'blur(4px)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 9999
        }}>
          <div style={{
            background: '#ffffff',
            borderRadius: '16px',
            border: '1px solid #e2e8f0',
            width: '100%',
            maxWidth: '480px',
            padding: '24px',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
          }}>
            <h3 style={{ fontSize: '18px', fontWeight: '700', color: '#0f172a', margin: '0 0 12px 0', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <RefreshCw size={20} color="#dc2626" /> Reset Sequence: {resetType.replace('_', ' ')}
            </h3>

            {resetWarning ? (
              <div style={{ background: '#fef2f2', border: '1px solid #fee2e2', borderRadius: '10px', padding: '16px', marginBottom: '20px' }}>
                <span style={{ fontSize: '13px', fontWeight: '600', color: '#991b1b', display: 'block', marginBottom: '4px' }}>⚠️ Duplicate ID Warning</span>
                <p style={{ color: '#b91c1c', fontSize: '13px', margin: 0 }}>
                  {resetWarning}
                </p>
                {canForce ? (
                  <p style={{ color: '#7f1d1d', fontSize: '12px', marginTop: '10px', fontWeight: '600' }}>
                    As a Super Admin, you can choose to bypass this check and Force Reset.
                  </p>
                ) : (
                  <p style={{ color: '#7f1d1d', fontSize: '12px', marginTop: '10px', fontWeight: '600' }}>
                    Only a Super Admin is authorized to force reset this sequence.
                  </p>
                )}
              </div>
            ) : (
              <p style={{ color: '#64748b', fontSize: '13.5px', marginBottom: '20px', lineHeight: '1.5' }}>
                This will reset the next generated sequential number. Ensure this action is permitted by your institution.
              </p>
            )}

            <form onSubmit={handleResetSubmit}>
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#475569', marginBottom: '4px' }}>Reset Start Value</label>
                <input
                  type="number"
                  min="1"
                  value={resetNextVal}
                  onChange={e => setResetNextVal(Math.max(1, Number(e.target.value)))}
                  style={{ width: '100%', padding: '10px 12px', border: '1px solid #cbd5e1', borderRadius: '8px', fontSize: '13.5px' }}
                  required
                  disabled={!!resetWarning && !canForce}
                />
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#475569', marginBottom: '4px' }}>Reason for Reset (Required for Audit Logging)</label>
                <textarea
                  rows="3"
                  placeholder="e.g. Starting New Academic Year 2026-27..."
                  value={resetReason}
                  onChange={e => setResetReason(e.target.value)}
                  style={{ width: '100%', padding: '10px 12px', border: '1px solid #cbd5e1', borderRadius: '8px', fontSize: '13.5px', resize: 'vertical' }}
                  required
                  disabled={!!resetWarning && !canForce}
                />
              </div>

              {resetWarning && canForce && (
                <div style={{ marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <input
                    type="checkbox"
                    id="forceResetCheckbox"
                    checked={resetForce}
                    onChange={e => setResetForce(e.target.checked)}
                    style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                  />
                  <label htmlFor="forceResetCheckbox" style={{ fontSize: '13px', fontWeight: '600', color: '#991b1b', cursor: 'pointer' }}>
                    I understand the risks and want to Force Reset this sequence
                  </label>
                </div>
              )}

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                <button
                  type="button"
                  onClick={() => {
                    setResetModalOpen(false);
                    setResetWarning('');
                    setResetForce(false);
                  }}
                  style={{
                    padding: '10px 16px',
                    background: '#f1f5f9',
                    color: '#475569',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '13px',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>
                {(!resetWarning || (resetWarning && canForce)) && (
                  <button
                    type="submit"
                    disabled={resetLoading || (!!resetWarning && !resetForce)}
                    style={{
                      padding: '10px 20px',
                      background: resetWarning ? '#dc2626' : '#2563eb',
                      color: '#ffffff',
                      border: 'none',
                      borderRadius: '8px',
                      fontSize: '13px',
                      fontWeight: '600',
                      cursor: (resetLoading || (!!resetWarning && !resetForce)) ? 'not-allowed' : 'pointer',
                      opacity: (resetLoading || (!!resetWarning && !resetForce)) ? 0.6 : 1,
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px'
                    }}
                  >
                    {resetLoading ? 'Processing...' : resetWarning ? 'Force Reset Sequence' : 'Reset Sequence'}
                  </button>
                )}
              </div>
            </form>
          </div>
        </div>
      )}

    </div>
  );
};

export default SettingsManagement;
